import {
  PANEL_COLORS,
  UF_NAMES,
  compareElectionValues,
  formatElectionLabel,
  getCargoMeta,
  getElectionValue,
} from './painel-meta.js';
import {
  createInitialPainelState,
} from './painel-state.js';
import {
  createPainelApi,
} from './painel-api.js';
import {
  buildChartSummaryHtml,
  syncCanvasAccessibility,
} from './painel-a11y.js';
import {
  SAVED_ANALYSES_STORAGE_KEY,
  buildAnalysisDedupeKey,
  buildCopyLabel as buildSavedCopyLabel,
  createSavedAnalysisId,
  getAnalysisDisplayLabel,
  getAnalysisSubLabel as getSavedAnalysisSubLabel,
  normalizeSavedAnalysisRecord,
  slugifyFilename as slugifySavedAnalysisFilename,
} from './painel-saved-analyses.js';
import {
  buildPoliticalDeltaData,
} from './painel-party-lineage.js';
import {
  aggregateAll,
  aggregateByZona,
  buildCandidateListViewModel,
  buildCompetitivenessIndex,
  buildPoliticalStrengthData,
  buildRankingViewModel,
  buildSectionCompetitivenessData,
  chooseDefaultCargoKey,
  extractCargoKeys,
  extractZonas,
  getCandidateCanonicalIdentity,
  getCandidateOfficialId,
  normalizeCargoKey,
  serverAggregateToPanelAggregate,
} from './painel-data.js';
import {
  buildComparativoData,
} from './painel-comparativo.js';
import {
  buildElectionComparisonData,
  buildElectionPairOptions,
  buildElectionPairSelectModel,
  formatElectionPairLabel,
} from './painel-temporal.js';
import {
  buildCandidateDossierData,
  findHistoricalCandidateMatch,
} from './painel-candidato.js';
import {
  renderTerritoryOpportunities,
} from './painel-territorios.js';

export {
  PANEL_COLORS,
  UF_NAMES,
  compareElectionValues,
  formatElectionLabel,
  getCargoMeta,
  getElectionValue,
} from './painel-meta.js';
export {
  DEFAULT_PARTY_CATALOG,
  createInitialPainelState,
} from './painel-state.js';
export {
  createPainelApi,
} from './painel-api.js';
export {
  buildPoliticalDeltaData,
  getPartyLineageInfo,
} from './painel-party-lineage.js';
export {
  aggregateAll,
  aggregateByZona,
  buildCandidateListViewModel,
  buildCompetitivenessIndex,
  buildPoliticalStrengthData,
  buildRankingViewModel,
  buildSectionCompetitivenessData,
  buildTerritoryOpportunityData,
  serverAggregateToPanelAggregate,
} from './painel-data.js';
export {
  buildComparativoData,
} from './painel-comparativo.js';
export {
  buildElectionComparisonData,
  buildElectionPairOptions,
  buildElectionPairSelectModel,
} from './painel-temporal.js';
export {
  buildCandidateDossierData,
  findHistoricalCandidateMatch,
} from './painel-candidato.js';
export {
  renderTerritoryOpportunities,
} from './painel-territorios.js';

/**
 * @module painel
 * @description Painel de Análise Eleitoral — Lógica de navegação, auth,
 * carregamento de dados e renderização de gráficos/tabelas.
 * Standalone: não depende de config.js/map modules do mapa.
 */

// ==========================================
// AUTH — reutiliza sessionStorage do login principal
// ==========================================
const AUTH = {
  get usuario() { return sessionStorage.getItem('gvi_usuario'); },
  get municipios() {
    try { return JSON.parse(sessionStorage.getItem('gvi_municipios') || '[]'); } catch { return []; }
  },
  get eleicoes() {
    try { return JSON.parse(sessionStorage.getItem('gvi_eleicoes') || '[]'); } catch { return []; }
  },
  async isLoggedIn() {
    try {
      const res = await fetch('/api/sessao', { credentials: 'same-origin' });
      return res.ok;
    } catch {
      return false;
    }
  },
  async logout() {
    try {
      await fetch('/api/logout', { method: 'POST', credentials: 'same-origin' });
    } catch {}
    sessionStorage.clear();
    window.location.href = '/';
  },
  headers() {
    return { 'Content-Type': 'application/json' };
  }
};


// ==========================================
// STATE
// ==========================================
const STATE = createInitialPainelState();
const PANEL_API = createPainelApi({ state: STATE, notify: showToast });
const { fetchDados, fetchAgregado } = PANEL_API;

const GLOBAL_CONTEXT_STORAGE_KEY = 'gvi_painel_recorte_v1';
const SECONDARY_SCOPES = Object.freeze(['zona', 'cand', 'comp']);
const MIN_CONTEXT_LOADING_MS = 240;

const START_EXAMPLES = Object.freeze({
  'pilao-arcado': {
    uf: 'BA',
    municipio: 'PILAO ARCADO',
    ano: '2024',
    cargoKey: 'prefeitos',
    label: 'Pilão Arcado/BA 2024'
  }
});

const PANEL_ICONS = Object.freeze({
  chart: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4 19V5m0 14h16M8 16V9m4 7V6m4 10v-4"/></svg>',
  home: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m3 10.5 9-7.5 9 7.5"/><path stroke-linecap="round" stroke-linejoin="round" d="M5.25 9.75V21h13.5V9.75M9 21v-6h6v6"/></svg>',
  bookmark: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0Z"/></svg>',
  compare: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5"/></svg>',
  building: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3H21m-3.75 3H21"/></svg>',
  pin: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"/></svg>',
  user: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"/></svg>',
  save: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M16.5 3.75V7.5h-9V3.75m11.25 16.5H5.25A2.25 2.25 0 0 1 3 18V6a2.25 2.25 0 0 1 2.25-2.25h10.5L21 9v9a2.25 2.25 0 0 1-2.25 2.25Z"/><path stroke-linecap="round" stroke-linejoin="round" d="M7.5 20.25v-6h9v6"/></svg>',
});

const PANEL_BUTTON_LABELS = Object.freeze({
  mun: { text: 'Analisar por Município', icon: 'chart' },
  zona: { text: 'Analisar por Zona', icon: 'chart' },
});

// ==========================================
// INIT
// ==========================================
document.addEventListener('DOMContentLoaded', () => {
  initAccessNav();
  initSidebar();
  initExecutiveSummaryToggles();
  initProfileChartToggles();
  initProfileChartViewToggles();
  initDropdowns();
  initCustomSelects();
  initButtons();
  initStartGuide();
  initGlobalContext();
  initEmptyStateActions();
  initLogout();
  renderInitialEmptyStates();
  loadSavedAnalyses();
});

// ==========================================
// SIDEBAR NAVIGATION
// ==========================================
function initSidebar() {
  const items = document.querySelectorAll('.pn-sidebar-item[data-section], .pn-mobile-secondary-action[data-section]');
  items.forEach(item => {
    item.addEventListener('click', () => {
      const section = item.getAttribute('data-section');
      selectPanelSection(section);
    });
  });
  updateSidebarCurrentState('municipio');
}

function initAccessNav() {
  const logoutLink = document.getElementById('pn-btn-logout');
  if (!logoutLink || AUTH.usuario) return;

  setNavLinkContent(logoutLink, 'home', 'Início', 'Início');
  logoutLink.setAttribute('href', '/');
  logoutLink.dataset.publicNav = 'true';
}

function setNavLinkContent(link, iconName, label, mobileLabel = label) {
  link.textContent = '';
  link.dataset.mobileLabel = mobileLabel;
  link.setAttribute('aria-label', label);
  link.setAttribute('title', label);

  const iconEl = document.createElement('span');
  iconEl.className = 'pn-nav-icon';
  iconEl.setAttribute('aria-hidden', 'true');
  iconEl.innerHTML = iconSvg(iconName);

  const labelEl = document.createElement('span');
  labelEl.className = 'pn-nav-label';
  labelEl.textContent = label;

  link.appendChild(iconEl);
  link.appendChild(labelEl);
}

function initGlobalContext() {
  ['pn-sel-uf-mun', 'pn-sel-municipio-mun', 'pn-sel-eleicao-mun', 'pn-sel-cargo-mun'].forEach(id => {
    const field = document.getElementById(id);
    field?.addEventListener('change', () => updateGlobalContext({ syncSecondary: true, persist: true }));
  });

  document.querySelectorAll('[data-global-context-edit]').forEach(button => {
    button.addEventListener('click', () => focusGlobalContextEditor());
  });
  document.querySelector('[data-zone-action="largest"]')?.addEventListener('click', () => selectLargestZoneFromGlobalContext());
  document.querySelector('[data-candidate-action="focus-search"]')?.addEventListener('click', () => focusCandidateSearchFromGlobalContext());
  document.querySelectorAll('[data-comp-start-action]').forEach(button => {
    button.addEventListener('click', () => startComparativoFromHint(button.dataset.compStartAction));
  });

  updateGlobalContext({ syncSecondary: false, persist: false });
}

function getGlobalRecorte() {
  const uf = document.getElementById('pn-sel-uf-mun')?.value || '';
  const municipio = document.getElementById('pn-sel-municipio-mun')?.value.trim() || '';
  const ano = document.getElementById('pn-sel-eleicao-mun')?.value || '';
  const cargoKey = document.getElementById('pn-sel-cargo-mun')?.value || '';
  const cargoLabel = getSelectedOptionText('pn-sel-cargo-mun') || (cargoKey ? getCargoMeta(cargoKey).label : '');
  return {
    uf,
    municipio,
    ano,
    cargoKey,
    cargoLabel,
    complete: Boolean(uf && municipio && ano && cargoKey),
    hasAny: Boolean(uf || municipio || ano || cargoKey),
  };
}

function getSelectedOptionText(id) {
  const select = document.getElementById(id);
  if (!select) return '';
  return select.options[select.selectedIndex]?.textContent?.trim() || '';
}

function updateGlobalContext(options = {}) {
  const recorte = getGlobalRecorte();
  const previousKey = STATE.recorte ? getRecorteKey(STATE.recorte) : '';
  const nextKey = getRecorteKey(recorte);
  STATE.recorte = recorte;

  renderGlobalContext(recorte);

  if (options.persist) persistGlobalContext(recorte);
  if (options.syncSecondary && recorte.complete) {
    syncSecondaryScopesWithGlobal(recorte);
    if (previousKey && previousKey !== nextKey) resetComparativo();
  }

  updateSectionHints(recorte);
  return recorte;
}

function getRecorteKey(recorte) {
  return [recorte?.uf || '', recorte?.municipio || '', recorte?.ano || '', recorte?.cargoKey || ''].join('|');
}

function getMunicipioDisplayLabel(municipio, uf = '') {
  const normalizedMunicipio = normalizeText(municipio);
  if (!normalizedMunicipio) return '';

  const catalog = STATE.catalogo?.municipios_por_uf || {};
  const scopedMunicipios = uf && Array.isArray(catalog[uf]) ? catalog[uf] : [];
  const allMunicipios = scopedMunicipios.length ? scopedMunicipios : Object.values(catalog).flat();
  const catalogMatch = allMunicipios.find(item => normalizeText(item) === normalizedMunicipio);

  return catalogMatch || municipio;
}

function renderGlobalContext(recorte) {
  const container = document.getElementById('pn-global-context');
  const shouldShow = recorte.complete;
  if (container) container.hidden = !shouldShow;
  document.body.classList.toggle('has-global-context', shouldShow);
  renderSidebarRecorte(recorte);

  if (!container || !shouldShow) return;

  const municipioLabel = getMunicipioDisplayLabel(recorte.municipio, recorte.uf);
  setText('pn-global-context-title', `${municipioLabel} · ${recorte.uf}`);
  setText('pn-global-context-uf', `${recorte.uf} — ${UF_NAMES[recorte.uf] || recorte.uf}`);
  setText('pn-global-context-municipio', municipioLabel);
  setText('pn-global-context-eleicao', formatElectionLabel(recorte.ano));
  setText('pn-global-context-cargo', recorte.cargoLabel || getCargoMeta(recorte.cargoKey).label);
}

function renderSidebarRecorte(recorte) {
  const container = document.getElementById('pn-sidebar-recut');
  if (!container) return;

  const shouldShow = Boolean(recorte?.complete);
  container.hidden = !shouldShow;
  if (!shouldShow) return;

  const municipioLabel = getMunicipioDisplayLabel(recorte.municipio, recorte.uf);
  setText('pn-sidebar-recut-title', `${municipioLabel} · ${recorte.uf}`);
  setText(
    'pn-sidebar-recut-meta',
    `${formatElectionLabel(recorte.ano)} · ${recorte.cargoLabel || getCargoMeta(recorte.cargoKey).label}`
  );
}

function setText(id, value) {
  const el = document.getElementById(id);
  if (el) el.textContent = value || '';
}

function persistGlobalContext(recorte) {
  try {
    if (!recorte.hasAny) {
      localStorage.removeItem(GLOBAL_CONTEXT_STORAGE_KEY);
      return;
    }
    localStorage.setItem(GLOBAL_CONTEXT_STORAGE_KEY, JSON.stringify({
      uf: recorte.uf,
      municipio: recorte.municipio,
      ano: recorte.ano,
      cargoKey: recorte.cargoKey,
    }));
  } catch { /* localStorage can be unavailable in private contexts */ }
}

async function restoreGlobalContextFromStorage() {
  let saved = null;
  try {
    saved = JSON.parse(localStorage.getItem(GLOBAL_CONTEXT_STORAGE_KEY) || 'null');
  } catch {
    saved = null;
  }
  if (!saved || !saved.uf || !saved.municipio) return;
  if (document.getElementById('pn-sel-municipio-mun')?.value) return;

  setSelectValue('pn-sel-uf-mun', saved.uf, `${saved.uf} — ${UF_NAMES[saved.uf] || saved.uf}`, true);
  setMunicipioField('mun', saved.municipio, { emitInput: false, dispatchChange: true });
  atualizarEleicoesMunicipio();
  if (saved.ano) setSelectValue('pn-sel-eleicao-mun', saved.ano, formatElectionLabel(saved.ano), true);
  if (saved.ano && saved.cargoKey) {
    await ensureCargoOptionsForScope('mun', saved.cargoKey);
    setSelectValue('pn-sel-cargo-mun', saved.cargoKey, getCargoMeta(saved.cargoKey).label, true);
  }
  closeMunicipioTypeaheads();
  updateGlobalContext({ syncSecondary: true, persist: false });
}

function syncSecondaryScopesWithGlobal(recorte = getGlobalRecorte()) {
  if (!recorte.complete) return;
  SECONDARY_SCOPES.forEach(scope => syncScopeWithGlobal(scope, recorte));
}

function syncScopeWithGlobal(scope, recorte = getGlobalRecorte()) {
  if (!recorte.complete) return;
  setSelectValue(`pn-sel-uf-${scope}`, recorte.uf, `${recorte.uf} — ${UF_NAMES[recorte.uf] || recorte.uf}`, false);
  setMunicipioField(scope, recorte.municipio, { emitInput: false, dispatchChange: false });
  setSelectValue(`pn-sel-eleicao-${scope}`, recorte.ano, formatElectionLabel(recorte.ano), false);
  setSelectValue(`pn-sel-cargo-${scope}`, recorte.cargoKey, recorte.cargoLabel || getCargoMeta(recorte.cargoKey).label, false);
}

function updateSectionHints(recorte = getGlobalRecorte()) {
  const hasContext = recorte.complete;
  document.querySelectorAll('.pn-section-hint').forEach(hint => {
    hint.classList.toggle('has-context', hasContext);
  });
}

function getMunicipioFilterElements() {
  const section = document.getElementById('sec-municipio');
  return {
    section,
    guide: document.getElementById('pn-start-guide'),
    filterCards: section ? Array.from(section.querySelectorAll(':scope > .pn-card')) : [],
    analyzeButton: document.getElementById('pn-btn-analisar-mun'),
    feedback: document.getElementById('pn-feedback-mun'),
  };
}

function setMunicipioAnalysisCompact(compact) {
  const { section, guide, filterCards, analyzeButton, feedback } = getMunicipioFilterElements();
  if (!section) return;

  section.classList.toggle('is-analysis-compact', compact);
  filterCards.forEach(card => { card.hidden = compact; });
  if (analyzeButton) analyzeButton.hidden = compact;
  if (feedback && compact) feedback.hidden = true;
  if (guide) guide.hidden = compact || Boolean(STATE.municipioAnalise);

  document.querySelectorAll('[data-global-context-edit]').forEach(button => {
    button.setAttribute('aria-controls', 'sec-municipio');
    button.setAttribute('aria-expanded', String(!compact));
  });
}

function scrollMunicipioFiltersIntoView() {
  const fieldCard = document.getElementById('pn-sel-uf-mun')?.closest('.pn-card');
  if (!fieldCard) return;

  window.scrollTo({ top: window.scrollY, left: 0, behavior: 'auto' });
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      const offset = window.innerWidth < 768 ? 156 : 128;
      const top = Math.max(0, fieldCard.getBoundingClientRect().top + window.scrollY - offset);
      window.scrollTo({ top, left: 0, behavior: 'smooth' });
    });
  });
}

function focusGlobalContextEditor() {
  selectPanelSection('municipio');
  setMunicipioAnalysisCompact(false);
  scrollMunicipioFiltersIntoView();
  window.setTimeout(() => focusPanelField('pn-sel-uf-mun'), 120);
}

async function selectLargestZoneFromGlobalContext() {
  const recorte = updateGlobalContext({ syncSecondary: true, persist: true });
  if (!recorte.complete) {
    focusGlobalContextEditor();
    showInlineFeedback('zona', 'Defina UF, município, eleição e cargo no recorte global antes de escolher a zona.');
    return;
  }

  selectPanelSection('zona', { refreshZona: false });
  syncScopeWithGlobal('zona', recorte);
  await atualizarZonasPainel();

  const dados = await fetchDados(recorte.municipio, recorte.ano);
  if (!dados) return;
  const zoneCounts = {};
  Object.keys(dados.secoes || {}).forEach(key => {
    const zone = String(key).split('_')[0];
    if (!zone) return;
    zoneCounts[zone] = (zoneCounts[zone] || 0) + 1;
  });
  const largestZone = Object.entries(zoneCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
  if (!largestZone) {
    showInlineFeedback('zona', 'Nenhuma zona foi encontrada para este recorte.');
    return;
  }

  setSelectValue('pn-sel-zona', largestZone, `Zona ${largestZone}`, true);
  await analisarZona(recorte.municipio, recorte.ano, largestZone, { cargoKey: recorte.cargoKey });
}

function focusCandidateSearchFromGlobalContext() {
  const recorte = updateGlobalContext({ syncSecondary: true, persist: true });
  if (!recorte.complete) {
    focusGlobalContextEditor();
    showInlineFeedback('cand', 'Defina UF, município, eleição e cargo no recorte global antes de buscar candidato.');
    return;
  }

  selectPanelSection('candidato');
  syncScopeWithGlobal('cand', recorte);
  document.getElementById('pn-search-cand')?.focus();
}

async function startComparativoFromHint(action = 'choose') {
  const recorte = updateGlobalContext({ syncSecondary: true, persist: true });
  if (!recorte.complete) {
    focusGlobalContextEditor();
    showInlineFeedback('comp', 'Defina UF, município, eleição e cargo no recorte global antes de comparar.');
    return;
  }

  selectPanelSection('comparativo');
  syncScopeWithGlobal('comp', recorte);
  await iniciarComparativo();
  if (action === 'leaders' || action === 'top3') applyComparativoQuickAction(action);
}

function iconSvg(name) {
  return PANEL_ICONS[name] || PANEL_ICONS.chart;
}

function buttonInnerHtml(label, iconName = 'chart') {
  return `<span class="pn-btn-icon" aria-hidden="true">${iconSvg(iconName)}</span><span>${esc(label)}</span>`;
}

function waitMinimumLoading(startedAt, minMs = MIN_CONTEXT_LOADING_MS) {
  const elapsed = Date.now() - startedAt;
  const remaining = minMs - elapsed;
  return remaining > 0 ? new Promise(resolve => window.setTimeout(resolve, remaining)) : Promise.resolve();
}

function emptyStateHtml({ scope, icon = 'chart', title, text, action = '', actionLabel = '', extra = '' }) {
  const actionHtml = action && actionLabel
    ? `<button type="button" class="pn-empty-action" data-empty-action="${esc(action)}">${esc(actionLabel)}</button>`
    : '';

  return `
    <div class="pn-empty pn-empty-rich" data-empty-state="${esc(scope)}">
      <span class="pn-empty-icon" aria-hidden="true">${iconSvg(icon)}</span>
      <strong>${esc(title)}</strong>
      <p>${esc(text)}</p>
      ${extra}
      ${actionHtml ? `<div class="pn-empty-actions">${actionHtml}</div>` : ''}
    </div>
  `;
}

function renderInitialEmptyStates() {
  renderAnalysisEmptyState('mun');
  renderAnalysisEmptyState('zona');
  renderCandidateEmptyState();
  renderComparativoEmptyState();
}

function initEmptyStateActions() {
  document.addEventListener('click', (e) => {
    const action = e.target.closest('[data-empty-action]');
    if (!action) return;
    e.preventDefault();

    const type = action.dataset.emptyAction;
    if (type === 'load-example') {
      document.querySelector('[data-start-example="pilao-arcado"]')?.click();
      return;
    }

    if (type === 'focus-global-context') {
      focusGlobalContextEditor();
      return;
    }

    if (type === 'largest-zone') {
      selectLargestZoneFromGlobalContext();
      return;
    }

    if (type === 'focus-candidate') {
      selectPanelSection('candidato');
      document.getElementById('pn-search-cand')?.focus();
      return;
    }

    if (type === 'start-comparativo') {
      startComparativoFromHint('choose');
    }
  });
}

function renderAnalysisEmptyState(scope, variant = 'initial') {
  const prefix = scope === 'zona' ? 'zona' : 'mun';
  const isZona = prefix === 'zona';
  const results = document.getElementById(`pn-results-${prefix}`);
  const slot = document.getElementById(`pn-empty-${prefix}`);
  if (!results || !slot) return;

  results.classList.add('visible');
  results.classList.remove('is-loading');
  results.setAttribute('aria-busy', 'false');

  const noData = variant === 'no-data';
  slot.hidden = false;
  slot.innerHTML = emptyStateHtml({
    scope: isZona ? 'zona' : 'municipio',
    icon: isZona ? 'pin' : 'building',
    title: noData
      ? 'Nenhum voto encontrado neste recorte'
      : (isZona ? 'Escolha uma zona para aprofundar' : 'Comece pelo recorte eleitoral'),
    text: noData
      ? 'A origem carregada não possui votos para o cargo selecionado. Ajuste cargo, eleição ou município.'
      : (isZona
        ? 'Use o recorte global e selecione uma zona para ver KPIs, ranking e força política local.'
        : 'Defina UF, município, eleição e cargo para transformar filtros em KPIs, ranking e gráficos.'),
    action: isZona ? 'largest-zone' : 'load-example',
    actionLabel: isZona ? 'Ver maior zona' : 'Carregar exemplo'
  });

  const summaryEl = document.getElementById(`pn-summary-${prefix}`);
  if (summaryEl) {
    summaryEl.replaceChildren();
    summaryEl.hidden = true;
  }
  document.getElementById(`pn-cargos-${prefix}`)?.replaceChildren();
  document.getElementById(`pn-kpis-${prefix}`)?.replaceChildren();
  document.getElementById(`pn-competitividade-${prefix}`)?.replaceChildren();
  document.getElementById(`pn-territorios-${prefix}`)?.replaceChildren();
  document.getElementById(`pn-ranking-${prefix}`)?.replaceChildren();
  document.getElementById(`pn-forca-politica-${prefix}`)?.replaceChildren();
  if (!isZona) {
    ['genero', 'idade', 'instrucao'].forEach(name => {
      clearProfileChartSkeleton(name);
      document.getElementById(`pn-summary-${name}`)?.replaceChildren();
    });
  }
}

function clearAnalysisEmptyState(prefix) {
  const slot = document.getElementById(`pn-empty-${prefix}`);
  if (!slot) return;
  slot.replaceChildren();
  slot.hidden = true;
}

function renderCandidateEmptyState(variant = 'initial') {
  const container = document.getElementById('pn-results-cand');
  if (!container) return;
  container.classList.remove('is-loading');
  container.setAttribute('aria-busy', 'false');

  const notFound = variant === 'not-found';
  container.innerHTML = emptyStateHtml({
    scope: 'candidato',
    icon: 'user',
    title: notFound ? 'Nenhum candidato encontrado' : 'Busque um candidato',
    text: notFound
      ? 'Tente buscar por outro nome, número ou partido dentro do recorte ativo.'
      : 'Digite ao menos 2 caracteres para localizar candidato por nome, número ou partido.',
    action: 'focus-candidate',
    actionLabel: 'Ir para busca'
  });
}

function renderComparativoEmptyState() {
  if (STATE.comparativoModo === 'eleicoes') {
    renderElectionComparisonEmptyState();
    return;
  }

  const container = document.getElementById('pn-results-comp');
  if (!container) return;
  container.classList.remove('is-loading');
  container.setAttribute('aria-busy', 'false');
  container.innerHTML = emptyStateHtml({
    scope: 'comparativo',
    icon: 'compare',
    title: 'Monte um comparativo',
    text: 'Escolha o recorte e inicie um comparativo para comparar líderes, top 3 ou candidatos específicos.',
    action: 'start-comparativo',
    actionLabel: 'Escolher candidatos'
  });
}

function renderElectionComparisonEmptyState(variant = 'initial') {
  const container = document.getElementById('pn-results-comp');
  if (!container) return;
  container.classList.remove('is-loading');
  container.setAttribute('aria-busy', 'false');
  const noData = variant === 'no-data';
  container.innerHTML = emptyStateHtml({
    scope: 'comparativo-eleicoes',
    icon: 'compare',
    title: noData ? 'Não foi possível comparar as eleições' : 'Compare a evolução eleitoral',
    text: noData
      ? 'Uma das eleições selecionadas não retornou dados públicos para este município.'
      : 'Escolha duas eleições do mesmo município para ver variação de votos, partido dominante e cargo comparável.',
    action: 'focus-global-context',
    actionLabel: 'Ajustar recorte'
  });
}

function initProfileChartToggles() {
  document.querySelectorAll('.pn-profile-chart-card').forEach(card => {
    const button = card.querySelector('.pn-chart-toggle');
    if (!button) return;

    button.addEventListener('click', () => {
      const expanded = button.getAttribute('aria-expanded') !== 'true';
      setProfileChartExpanded(card, expanded);
      if (expanded) {
        const chartName = card.getAttribute('data-profile-chart');
        window.requestAnimationFrame(() => STATE.charts[chartName]?.resize?.());
      }
    });
  });

  window.addEventListener('resize', () => syncProfileChartDisclosureForViewport());
}

function initExecutiveSummaryToggles() {
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-summary-toggle]');
    if (!button) return;

    const summary = button.closest('.pn-executive-summary');
    if (!summary) return;

    const expanded = button.getAttribute('aria-expanded') !== 'true';
    setExecutiveSummaryExpanded(summary, expanded);
  });

  window.addEventListener('resize', () => syncExecutiveSummaryDisclosureForViewport());
}

function initProfileChartViewToggles() {
  document.querySelectorAll('[data-chart-view][data-view]').forEach(button => {
    button.addEventListener('click', () => {
      setProfileChartView(button.dataset.chartView, button.dataset.view);
    });
  });
}

function setProfileChartView(chartName, view) {
  const normalizedView = view === 'table' ? 'table' : 'chart';
  const card = document.querySelector(`.pn-profile-chart-card[data-profile-chart="${chartName}"]`);
  if (!card) return;
  card.setAttribute('data-active-view', normalizedView);
  card.querySelectorAll('[data-chart-view][data-view]').forEach(button => {
    const active = button.dataset.view === normalizedView;
    button.classList.toggle('active', active);
    button.setAttribute('aria-pressed', String(active));
  });

  const canvas = card.querySelector('canvas');
  const chartSurface = canvas?.closest('.pn-education-chart-frame') || canvas;
  if (chartSurface) {
    if (normalizedView === 'table') {
      chartSurface.setAttribute('hidden', '');
    } else {
      chartSurface.removeAttribute('hidden');
    }
  }

  if (normalizedView === 'chart') {
    window.requestAnimationFrame(() => STATE.charts[chartName]?.resize?.());
  }
}

function setProfileChartsExpanded(expanded) {
  document.querySelectorAll('.pn-profile-chart-card').forEach(card => setProfileChartExpanded(card, expanded));
}

function setProfileChartExpanded(card, expanded) {
  const button = card?.querySelector('.pn-chart-toggle');
  const body = card?.querySelector('.pn-chart-body');
  if (!button || !body) return;
  button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  body.hidden = !expanded;
  card.classList.toggle('is-collapsed', !expanded);
}

function syncProfileChartDisclosureForViewport(options = {}) {
  const mobile = window.matchMedia('(max-width: 768px)').matches;
  const reset = options.reset === true;
  const cards = Array.from(document.querySelectorAll('.pn-profile-chart-card'));

  if (!mobile) {
    cards.forEach(card => setProfileChartExpanded(card, true));
    return;
  }

  if (!reset) return;

  cards.forEach(card => {
    const chartName = card.getAttribute('data-profile-chart');
    setProfileChartExpanded(card, chartName === 'genero');
  });
}

// ==========================================
// DROPDOWNS — populate from /api/catalogo + session fallback
// ==========================================
function setExecutiveSummaryExpanded(summary, expanded) {
  const button = summary?.querySelector('[data-summary-toggle]');
  if (!button) return;

  summary.classList.toggle('is-mobile-collapsed', !expanded);
  button.setAttribute('aria-expanded', expanded ? 'true' : 'false');

  const label = button.querySelector('[data-summary-toggle-label]');
  if (label) label.textContent = expanded ? 'Ocultar detalhes' : 'Ver detalhes';
}

function syncExecutiveSummaryDisclosureForViewport() {
  const mobile = window.matchMedia('(max-width: 768px)').matches;

  document.querySelectorAll('.pn-executive-summary').forEach(summary => {
    if (summary.hidden) return;
    setExecutiveSummaryExpanded(summary, !mobile);
  });
}

async function initDropdowns() {
  // 1. Fetch national catalog (async, non-blocking)
  fetchCatalogo();

  // 2. Populate election selects from session (immediate)
  const eleicoes = AUTH.eleicoes;
  ['pn-sel-eleicao-zona', 'pn-sel-eleicao-cand', 'pn-sel-eleicao-comp'].forEach(id => {
    const sel = document.getElementById(id);
    if (!sel) return;
    sel.innerHTML = '<option value="">Selecione a eleição</option>';
    [...(eleicoes || [])].sort(compareElectionValues).forEach(e => {
      const ano = getElectionValue(e);
      const permitida = typeof e === 'object' ? e.permitida !== false : true;
      if (!permitida) return;
      const opt = document.createElement('option');
      opt.value = ano;
      opt.textContent = formatElectionLabel(ano);
      sel.appendChild(opt);
    });
  });

  const selMun = document.getElementById('pn-sel-eleicao-mun');
  if (selMun) {
    selMun.innerHTML = '<option value="">Selecione o município primeiro</option>';
    selMun.disabled = true;
  }

  const munInput = document.getElementById('pn-sel-municipio-mun');
  if (munInput) {
    munInput.disabled = true;
    munInput.placeholder = "Selecione o estado primeiro";
    munInput.addEventListener('change', atualizarEleicoesMunicipio);
  }

  initMunicipioTypeaheads();
  initCargoSelects();
}

function getMunicipioTypeaheadConfigs() {
  return [
    { ufId: 'pn-sel-uf-mun', inputId: 'pn-sel-municipio-mun', dropdownId: 'pn-typeahead-mun' },
    { ufId: 'pn-sel-uf-zona', inputId: 'pn-sel-municipio-zona', dropdownId: 'pn-typeahead-zona' },
    { ufId: 'pn-sel-uf-cand', inputId: 'pn-sel-municipio-cand', dropdownId: 'pn-typeahead-cand' },
    { ufId: 'pn-sel-uf-comp', inputId: 'pn-sel-municipio-comp', dropdownId: 'pn-typeahead-comp' },
  ];
}

function populateUfSelects(ufs) {
  const sortedUfs = [...new Set(ufs || [])].sort();
  getMunicipioTypeaheadConfigs().forEach(({ ufId }) => {
    const select = document.getElementById(ufId);
    if (!select) return;
    const previousValue = select.value;
    select.innerHTML = '<option value="">Selecione o estado</option>';
    sortedUfs.forEach(uf => {
      const opt = document.createElement('option');
      opt.value = uf;
      opt.textContent = `${uf} — ${UF_NAMES[uf] || uf}`;
      select.appendChild(opt);
    });
    if (previousValue && sortedUfs.includes(previousValue)) {
      select.value = previousValue;
    }
  });
}

function initMunicipioTypeaheads() {
  getMunicipioTypeaheadConfigs().forEach(({ ufId, inputId, dropdownId }) => {
    const ufSelect = document.getElementById(ufId);
    const input = document.getElementById(inputId);
    if (!input) return;

    input.disabled = !(ufSelect && ufSelect.value);
    input.placeholder = input.disabled ? 'Selecione o estado primeiro' : 'Digite ou selecione o município...';

    if (ufSelect && !ufSelect.dataset.municipioTypeaheadBound) {
      ufSelect.dataset.municipioTypeaheadBound = 'true';
      ufSelect.addEventListener('change', () => {
        input.value = '';
        input.dataset.selected = '';
        input.disabled = !ufSelect.value;
        input.placeholder = ufSelect.value ? 'Digite ou selecione o município...' : 'Selecione o estado primeiro';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    initTypeahead(inputId, dropdownId, ufId);
  });
}

// ==========================================
// ELEIÇÕES DINÂMICAS POR MUNICÍPIO
// ==========================================
function populateElectionSelects(eleicoes) {
  ['pn-sel-eleicao-zona', 'pn-sel-eleicao-cand', 'pn-sel-eleicao-comp'].forEach(id => {
    const sel = document.getElementById(id);
    if (!sel) return;
    sel.innerHTML = '<option value="">Selecione a eleição</option>';
    [...(eleicoes || [])].sort(compareElectionValues).forEach(e => {
      const ano = getElectionValue(e);
      const permitida = typeof e === 'object' ? e.permitida !== false : true;
      if (!permitida) return;
      const opt = document.createElement('option');
      opt.value = ano;
      opt.textContent = formatElectionLabel(ano);
      sel.appendChild(opt);
    });
  });
}

function atualizarEleicoesMunicipio() {
  const ufSel = document.getElementById('pn-sel-uf-mun');
  const munInput = document.getElementById('pn-sel-municipio-mun');
  const eleicaoSel = document.getElementById('pn-sel-eleicao-mun');
  if (!eleicaoSel) return;

  const uf = ufSel ? ufSel.value : '';
  const mun = munInput ? munInput.value.trim() : '';

  if (!uf || !mun) {
    delete eleicaoSel.dataset.municipioKey;
    eleicaoSel.innerHTML = '<option value="">Selecione o município primeiro</option>';
    eleicaoSel.disabled = true;
    return;
  }

  const key = `${uf}:${mun}`;
  if (eleicaoSel.dataset.municipioKey === key && eleicaoSel.options.length > 1) {
    return;
  }

  let anosDisponiveis = [];

  if (STATE.catalogo && STATE.catalogo.eleicoes_por_municipio && STATE.catalogo.eleicoes_por_municipio[key]) {
    anosDisponiveis = STATE.catalogo.eleicoes_por_municipio[key];
  } else {
    // Fallback: usar eleições do catálogo ou da sessão
    if (STATE.catalogo && STATE.catalogo.eleicoes && STATE.catalogo.eleicoes.length > 0) {
      anosDisponiveis = STATE.catalogo.eleicoes;
    } else {
      anosDisponiveis = AUTH.eleicoes.map(e => typeof e === 'object' ? e.ano : e);
    }
  }

  // Ordena por ano e turno, preservando valores internos como 2022_2.
  anosDisponiveis.sort(compareElectionValues);

  eleicaoSel.innerHTML = '<option value="">Selecione a eleição</option>';
  eleicaoSel.disabled = false;
  eleicaoSel.dataset.municipioKey = key;

  anosDisponiveis.forEach(eleicao => {
    const ano = getElectionValue(eleicao);
    if (typeof AUTH.eleicoes[0] === 'object') {
      const authEleicao = AUTH.eleicoes.find(e => String(e.ano) === String(ano));
      if (authEleicao && authEleicao.permitida === false) return;
    }
    const opt = document.createElement('option');
    opt.value = ano;
    opt.textContent = formatElectionLabel(ano);
    eleicaoSel.appendChild(opt);
  });
}

// ==========================================
// CATÁLOGO NACIONAL — fetch and populate UF + typeahead
// ==========================================
async function fetchCatalogo() {
  try {
    STATE.catalogo = await PANEL_API.fetchCatalogo(AUTH.headers());
    if (STATE.catalogo.eleicoes && STATE.catalogo.eleicoes.length > 0) {
      populateElectionSelects(STATE.catalogo.eleicoes);
    }

    const ufs = STATE.catalogo.ufs?.length
      ? STATE.catalogo.ufs
      : Object.keys(STATE.catalogo.municipios_por_uf || {});
    populateUfSelects(ufs);
    await restoreGlobalContextFromStorage();

  } catch (e) {
    console.warn('Catálogo não disponível, usando dados da sessão:', e.message);
  }
}

// ==========================================
// TYPEAHEAD — municipality autocomplete
// ==========================================
function initTypeahead(inputId, dropdownId, ufSelectId) {
  const input = document.getElementById(inputId);
  const dropdown = document.getElementById(dropdownId);
  const ufSelect = document.getElementById(ufSelectId);
  if (!input || !dropdown) return;
  if (input.dataset.typeaheadInitialized === 'true') return;
  input.dataset.typeaheadInitialized = 'true';

  const maxVisibleItems = 80;
  const listboxId = dropdown.id || `${input.id}-listbox`;
  dropdown.id = listboxId;
  dropdown.setAttribute('role', 'listbox');
  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-autocomplete', 'list');
  input.setAttribute('aria-haspopup', 'listbox');
  input.setAttribute('aria-controls', listboxId);
  input.setAttribute('aria-expanded', 'false');
  if (!input.getAttribute('aria-label')) input.setAttribute('aria-label', 'Município');

  let activeIndex = -1;
  let typeaheadVersion = 0;

  const getOptions = () => Array.from(dropdown.querySelectorAll('.pn-ta-item[data-value]'));

  function setExpanded(isExpanded) {
    input.setAttribute('aria-expanded', String(isExpanded));
    if (!isExpanded) {
      activeIndex = -1;
      input.removeAttribute('aria-activedescendant');
    }
  }

  function setActiveIndex(index, scrollIntoView = false) {
    const options = getOptions();
    if (!options.length) {
      activeIndex = -1;
      input.removeAttribute('aria-activedescendant');
      return;
    }

    activeIndex = Math.max(0, Math.min(index, options.length - 1));
    options.forEach((option, optionIndex) => {
      const isActive = optionIndex === activeIndex;
      option.classList.toggle('active', isActive);
      option.setAttribute('aria-selected', String(isActive));
    });

    const activeOption = options[activeIndex];
    if (activeOption) {
      input.setAttribute('aria-activedescendant', activeOption.id);
      if (scrollIntoView) activeOption.scrollIntoView({ block: 'nearest' });
    }
  }

  function closeDropdown() {
    dropdown.style.display = 'none';
    setExpanded(false);
  }

  function selectTypeaheadItem(item) {
    if (!item?.dataset.value) return;
    typeaheadVersion += 1;
    input.value = item.dataset.value;
    input.dataset.selected = item.dataset.value;
    closeDropdown();
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function showDropdown(query = '') {
    const uf = ufSelect ? ufSelect.value : '';
    if (ufSelect && !uf) {
      dropdown.innerHTML = '';
      closeDropdown();
      return;
    }

    let candidates = [];
    if (STATE.catalogo && STATE.catalogo.municipios_por_uf) {
      if (uf && STATE.catalogo.municipios_por_uf[uf]) {
        candidates = STATE.catalogo.municipios_por_uf[uf];
      } else {
        candidates = Object.values(STATE.catalogo.municipios_por_uf).flat();
      }
    }

    // Fallback to session municipios
    if (candidates.length === 0) {
      let muns = AUTH.municipios;
      if (muns.length === 1 && muns[0] === '*') {
        try {
          const config = JSON.parse(sessionStorage.getItem('gvi_municipios_config') || '[]');
          muns = config.map(m => m.nome);
        } catch { /* ignore */ }
      }
      candidates = muns;
    }

    // Garantir ordem alfabética
    candidates = [...new Set(candidates)].sort((a, b) => a.localeCompare(b, 'pt-BR'));

    const queryNorm = query.trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

    let matches = candidates;
    if (queryNorm.length > 0) {
      matches = candidates.filter(m =>
        m.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().includes(queryNorm)
      );
    }

    if (matches.length === 0) {
      dropdown.innerHTML = '<div class="pn-ta-item pn-ta-empty" role="presentation">Nenhum município encontrado</div>';
      dropdown.style.display = 'block';
      setExpanded(true);
      setActiveIndex(-1);
      return;
    }

    const visibleMatches = matches.slice(0, maxVisibleItems);
    const hasMoreMatches = matches.length > visibleMatches.length;
    const moreMessage = hasMoreMatches
      ? `<div class="pn-ta-item pn-ta-more">Mostrando ${visibleMatches.length} de ${matches.length.toLocaleString('pt-BR')} municípios. Digite para filtrar.</div>`
      : '';

    dropdown.innerHTML = visibleMatches.map((m, index) =>
      `<div class="pn-ta-item" id="${escapeHTML(listboxId)}-option-${index}" role="option" aria-selected="false" data-value="${escapeHTML(m)}">${highlightMatch(m, queryNorm)}</div>`
    ).join('') + moreMessage;
    dropdown.style.display = 'block';
    setExpanded(true);
    setActiveIndex(0);

    // Click handler for items
    dropdown.querySelectorAll('.pn-ta-item[data-value]').forEach(item => {
      item.addEventListener('click', (e) => {
        e.stopPropagation();
        selectTypeaheadItem(item);
      });
    });
  }

  input.addEventListener('input', () => {
    const version = ++typeaheadVersion;
    if (input.value !== input.dataset.selected) {
      input.dataset.selected = '';
    }
    debounce(() => {
      if (version !== typeaheadVersion) return;
      if (input.value === input.dataset.selected) {
        closeDropdown();
        return;
      }
      showDropdown(input.value);
    }, 150)();
  });

  // Ao focar e ao clicar, abrir o dropdown com a lista completa ou filtrada
  input.addEventListener('focus', () => {
    showDropdown(input.value);
  });

  input.addEventListener('click', (e) => {
    e.stopPropagation();
    showDropdown(input.value);
  });

  input.addEventListener('keydown', (e) => {
    const options = getOptions();
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (input.getAttribute('aria-expanded') !== 'true') showDropdown(input.value);
        setActiveIndex(activeIndex + 1, true);
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (input.getAttribute('aria-expanded') !== 'true') showDropdown(input.value);
        setActiveIndex(activeIndex <= 0 ? options.length - 1 : activeIndex - 1, true);
        break;
      case 'Home':
        if (input.getAttribute('aria-expanded') === 'true') {
          e.preventDefault();
          setActiveIndex(0, true);
        }
        break;
      case 'End':
        if (input.getAttribute('aria-expanded') === 'true') {
          e.preventDefault();
          setActiveIndex(options.length - 1, true);
        }
        break;
      case 'Enter':
        if (input.getAttribute('aria-expanded') === 'true' && options[activeIndex]) {
          e.preventDefault();
          selectTypeaheadItem(options[activeIndex]);
        }
        break;
      case 'Escape':
        if (input.getAttribute('aria-expanded') === 'true') {
          e.preventDefault();
          closeDropdown();
        }
        break;
    }
  });

  // Close dropdown on outside click
  document.addEventListener('click', (e) => {
    if (e.target instanceof Node && !input.contains(e.target) && !dropdown.contains(e.target)) {
      closeDropdown();
    }
  });

  // Habilitar/desabilitar controle inicial
  if (ufSelect) {
    input.disabled = !ufSelect.value;
    input.placeholder = ufSelect.value ? "Digite ou selecione o município..." : "Selecione o estado primeiro";
  }
}

function highlightMatch(text, queryNorm) {
  if (!queryNorm) return escapeHTML(text);
  const textNorm = text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const idx = textNorm.indexOf(queryNorm);
  if (idx === -1) return escapeHTML(text);
  const before = text.slice(0, idx);
  const match = text.slice(idx, idx + queryNorm.length);
  const after = text.slice(idx + queryNorm.length);
  return `${escapeHTML(before)}<strong>${escapeHTML(match)}</strong>${escapeHTML(after)}`;
}

async function atualizarZonasPainel() {
  const mun = document.getElementById('pn-sel-municipio-zona')?.value.trim();
  const ano = document.getElementById('pn-sel-eleicao-zona')?.value;
  const sel = document.getElementById('pn-sel-zona');
  if (!sel) return;

  sel.innerHTML = '<option value="">Carregando...</option>';
  updateZoneReadyState();
  if (!mun || !ano) {
    updateZoneReadyState();
    sel.innerHTML = '<option value="">Selecione município e eleição</option>';
    return;
  }

  const dados = await fetchDados(mun, ano);
  if (!dados) {
    updateZoneReadyState();
    sel.innerHTML = '<option value="">Erro ao carregar</option>';
    return;
  }

  const zonas = extractZonas(dados);
  sel.innerHTML = '<option value="">Selecione a zona</option>';
  zonas.forEach(z => {
    const opt = document.createElement('option');
    opt.value = z;
    opt.textContent = `Zona ${z}`;
    sel.appendChild(opt);
  });
  syncCustomSelect(sel);
  updateZoneReadyState();
}

function updateZoneReadyState() {
  const section = document.getElementById('sec-zona');
  const selectedZone = document.getElementById('pn-sel-zona')?.value || '';
  section?.classList.toggle('is-zone-ready', Boolean(selectedZone));
}

function initCargoSelects() {
  ['mun', 'zona', 'cand', 'comp'].forEach(scope => resetCargoSelect(scope));
}

function resetCargoSelect(scope, message = 'Selecione município e eleição') {
  const select = document.getElementById(`pn-sel-cargo-${scope}`);
  if (!select) return;
  delete select.dataset.scopeKey;
  select.innerHTML = `<option value="">${esc(message)}</option>`;
  select.value = '';
  select.disabled = true;
  syncCustomSelect(select);
}

function getScopeValues(scope) {
  const recorte = STATE.recorte || getGlobalRecorte();
  if (scope !== 'mun' && recorte.complete) {
    return {
      uf: recorte.uf,
      ano: recorte.ano,
      municipio: recorte.municipio
    };
  }
  return {
    uf: document.getElementById(`pn-sel-uf-${scope}`)?.value || '',
    ano: document.getElementById(`pn-sel-eleicao-${scope}`)?.value || '',
    municipio: document.getElementById(`pn-sel-municipio-${scope}`)?.value.trim() || ''
  };
}

async function atualizarCargosPainel(scope, preferredCargoKey = '', options = {}) {
  const { municipio, ano } = getScopeValues(scope);
  if (!municipio || !ano) {
    resetCargoSelect(scope);
    return null;
  }

  const select = document.getElementById(`pn-sel-cargo-${scope}`);
  const scopeKey = `${municipio}_${ano}`;
  if (!preferredCargoKey && select?.dataset.scopeKey === scopeKey && !select.disabled && select.options.length > 1) {
    return STATE.dadosCache[scopeKey] || fetchDados(municipio, ano, { silent: options.silent !== false });
  }

  if (select) {
    select.disabled = true;
    select.innerHTML = '<option value="">Carregando cargos...</option>';
  }

  const dados = await fetchDados(municipio, ano, { silent: options.silent !== false });
  if (!dados) {
    resetCargoSelect(scope, 'Erro ao carregar cargos');
    return null;
  }

  populateCargoSelect(scope, extractCargoKeys(dados), preferredCargoKey || getSelectedCargoKey(scope));
  return dados;
}

async function ensureCargoOptionsForScope(scope, preferredCargoKey = '') {
  const { municipio, ano } = getScopeValues(scope);
  if (!municipio || !ano) {
    resetCargoSelect(scope);
    return null;
  }

  const select = document.getElementById(`pn-sel-cargo-${scope}`);
  if (select && !select.disabled && select.options.length > 1 && select.value) {
    return fetchDados(municipio, ano);
  }

  return atualizarCargosPainel(scope, preferredCargoKey, { silent: false });
}

function populateCargoSelect(scope, cargos, preferredCargoKey = '') {
  const select = document.getElementById(`pn-sel-cargo-${scope}`);
  if (!select) return;

  const available = [...new Set(cargos || [])].filter(Boolean);
  if (!available.length) {
    resetCargoSelect(scope, 'Nenhum cargo encontrado');
    return;
  }

  const current = preferredCargoKey || select.value;
  const selected = chooseDefaultCargoKey(available, current);
  select.innerHTML = '<option value="">Selecione o cargo</option>';
  available.forEach(cargoKey => {
    const opt = document.createElement('option');
    opt.value = cargoKey;
    opt.textContent = getCargoMeta(cargoKey).label;
    select.appendChild(opt);
  });
  select.disabled = false;
  select.value = selected;
  syncCustomSelect(select);

  const { municipio, ano } = getScopeValues(scope);
  if (municipio && ano) {
    select.dataset.scopeKey = `${municipio}_${ano}`;
  } else {
    delete select.dataset.scopeKey;
  }

  if (scope === 'mun') {
    updateGlobalContext({ syncSecondary: true, persist: true });
  }
}

function syncCargoSelect(scope, cargos, cargoKey) {
  const select = document.getElementById(`pn-sel-cargo-${scope}`);
  if (!select || !cargoKey) return;
  const hasOption = Array.from(select.options).some(opt => opt.value === cargoKey);
  if (!hasOption) {
    populateCargoSelect(scope, cargos, cargoKey);
    return;
  }
  select.disabled = false;
  select.value = cargoKey;
}

function getSelectedCargoKey(scope) {
  const recorte = STATE.recorte || getGlobalRecorte();
  if (scope !== 'mun' && recorte.complete) return recorte.cargoKey;
  return document.getElementById(`pn-sel-cargo-${scope}`)?.value || '';
}

// ==========================================
// BUTTONS — action handlers
// ==========================================
function initButtons() {
  // Município
  document.getElementById('pn-btn-analisar-mun')?.addEventListener('click', async () => {
    const ano = document.getElementById('pn-sel-eleicao-mun').value;
    const mun = document.getElementById('pn-sel-municipio-mun').value;
    const missingBeforeCargo = getMissingScopeFields('mun').filter(field => field.id !== 'pn-sel-cargo-mun');
    if (missingBeforeCargo.length) {
      validateScopeFields('mun');
      return;
    }

    const btn = document.getElementById('pn-btn-analisar-mun');
    const loadingStartedAt = Date.now();
    setButtonLoading(btn, 'Carregando dados do município...');
    renderAnalysisSkeleton('mun');
    try {
      await ensureCargoOptionsForScope('mun');
      if (!validateScopeFields('mun')) return;
      await analisarMunicipio(mun, ano, { cargoKey: getSelectedCargoKey('mun'), showLoading: false });
    } finally {
      await waitMinimumLoading(loadingStartedAt);
      resetButton(btn, PANEL_BUTTON_LABELS.mun);
      clearAnalysisLoading('mun');
    }
  });

  // Zona
  document.getElementById('pn-btn-analisar-zona')?.addEventListener('click', async () => {
    const ano = document.getElementById('pn-sel-eleicao-zona').value;
    const mun = document.getElementById('pn-sel-municipio-zona').value;
    const zona = document.getElementById('pn-sel-zona').value;
    await ensureCargoOptionsForScope('zona');
    if (!validateScopeFields('zona')) return;
    await analisarZona(mun, ano, zona, { cargoKey: getSelectedCargoKey('zona') });
  });

  document.getElementById('pn-sel-municipio-zona')?.addEventListener('change', atualizarZonasPainel);
  document.getElementById('pn-sel-eleicao-zona')?.addEventListener('change', atualizarZonasPainel);
  document.getElementById('pn-sel-zona')?.addEventListener('change', updateZoneReadyState);

  ['mun', 'zona', 'cand', 'comp'].forEach(scope => {
    document.getElementById(`pn-sel-municipio-${scope}`)?.addEventListener('change', () => atualizarCargosPainel(scope));
    document.getElementById(`pn-sel-eleicao-${scope}`)?.addEventListener('change', () => atualizarCargosPainel(scope));
  });

  document.getElementById('pn-sel-cargo-mun')?.addEventListener('change', () => {
    const cargoKey = getSelectedCargoKey('mun');
    if (STATE.municipioAnalise && cargoKey && STATE.municipioAnalise.cargos.includes(cargoKey)) {
      STATE.municipioAnalise.cargoKey = cargoKey;
      renderMunicipioAnalise();
    }
  });

  document.getElementById('pn-sel-cargo-zona')?.addEventListener('change', () => {
    const cargoKey = getSelectedCargoKey('zona');
    if (STATE.zonaAnalise && cargoKey && STATE.zonaAnalise.cargos.includes(cargoKey)) {
      STATE.zonaAnalise.cargoKey = cargoKey;
      renderZonaAnalise();
    }
  });

  document.getElementById('pn-sel-cargo-cand')?.addEventListener('change', handleCandidateSearchCurrent);
  document.getElementById('pn-sel-cargo-comp')?.addEventListener('change', () => {
    resetComparativo();
    refreshElectionPairOptions();
  });

  // Candidate search
  document.getElementById('pn-search-cand')?.addEventListener('input', debounce(async (e) => {
    await handleCandidateSearchCurrent(e);
  }, 300));

  // Comparativo
  document.getElementById('pn-btn-novo-comp')?.addEventListener('click', iniciarComparativo);
  document.querySelectorAll('[data-comp-mode]').forEach(button => {
    button.addEventListener('click', () => setComparativoMode(button.dataset.compMode || 'candidatos'));
  });
  document.getElementById('pn-btn-comparar-eleicoes')?.addEventListener('click', compararEleicoes);
  document.getElementById('pn-results-mun')?.addEventListener('click', handlePanelCargoClick);
  document.getElementById('pn-results-zona')?.addEventListener('click', handlePanelCargoClick);
  document.getElementById('pn-results-comp')?.addEventListener('click', handleComparativoClick);
  document.getElementById('pn-results-comp')?.addEventListener('input', handleComparativoInput);
  document.getElementById('pn-lista-analises')?.addEventListener('click', handleSavedAnalysisClick);
  document.getElementById('pn-lista-analises')?.addEventListener('keydown', handleSavedAnalysisKeydown);
  document.getElementById('pn-lista-analises')?.addEventListener('click', handleSavedEmptyAction);
  document.getElementById('pn-clear-analises')?.addEventListener('click', clearSavedAnalyses);

  ['pn-sel-eleicao-comp', 'pn-sel-municipio-comp'].forEach(id => {
    document.getElementById(id)?.addEventListener('change', () => {
      resetComparativo();
      refreshElectionPairOptions();
    });
  });

  initFeedbackResetHandlers();
}

function initStartGuide() {
  document.querySelectorAll('[data-start-example]').forEach(button => {
    button.addEventListener('click', () => runStartExample(button));
  });

  document.querySelector('[data-start-focus="municipio"]')?.addEventListener('click', () => {
    selectPanelSection('municipio');
    scrollMunicipioFiltersIntoView();
    window.setTimeout(() => focusPanelField('pn-sel-uf-mun'), 120);
  });
}

async function runStartExample(button) {
  const key = button?.dataset.startExample;
  const example = START_EXAMPLES[key];
  if (!example) return;

  const originalContent = button.innerHTML;
  button.disabled = true;
  button.innerHTML = `<span>Carregando exemplo</span><strong>${esc(example.label)}</strong>`;

  try {
    selectPanelSection('municipio');
    setSelectValue('pn-sel-uf-mun', example.uf, `${example.uf} — ${UF_NAMES[example.uf] || example.uf}`, true);
    setMunicipioField('mun', example.municipio, { emitInput: false });
    document.getElementById('pn-sel-municipio-mun')?.dispatchEvent(new Event('change', { bubbles: true }));
    atualizarEleicoesMunicipio();
    setSelectValue('pn-sel-eleicao-mun', example.ano, formatElectionLabel(example.ano), true);

    const dados = await ensureCargoOptionsForScope('mun', example.cargoKey);
    if (!dados) return;

    const cargoLabel = getCargoMeta(example.cargoKey).label;
    setSelectValue('pn-sel-cargo-mun', example.cargoKey, cargoLabel, true);
    closeMunicipioTypeaheads();
    updateGlobalContext({ syncSecondary: true, persist: true });
    clearScopeFeedback('mun');
    await analisarMunicipio(example.municipio, example.ano, { cargoKey: example.cargoKey, save: false });
    showToast(`Exemplo público carregado: ${example.label}.`, 'info');
    document.getElementById('pn-results-mun')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  } finally {
    button.disabled = false;
    button.innerHTML = originalContent;
  }
}

function focusPanelField(fieldId) {
  const field = document.getElementById(fieldId);
  const customTrigger = field?.closest('.pn-select-wrapper')?.querySelector('.pn-custom-select-trigger');
  const target = customTrigger || field;
  if (target && typeof target.focus === 'function') target.focus();
}

async function handleCandidateSearchCurrent(e) {
  const input = document.getElementById('pn-search-cand');
  const q = (e?.target?.id === 'pn-search-cand' ? e.target.value : input?.value || '').trim();
  const recorte = STATE.recorte || getGlobalRecorte();
  if (recorte.complete) syncScopeWithGlobal('cand', recorte);
  const ano = recorte.complete ? recorte.ano : document.getElementById('pn-sel-eleicao-cand')?.value;
  const mun = recorte.complete ? recorte.municipio : document.getElementById('pn-sel-municipio-cand')?.value;

  if (!ano || !mun || q.length < 2) {
    renderCandidateEmptyState();
    if (q.length >= 2) showInlineFeedback('cand', 'Selecione eleição, município e cargo para buscar candidato.', getMissingScopeFields('cand'));
    return;
  }

  const loadingStartedAt = Date.now();
  renderCandidateSkeleton();

  try {
    const dados = await ensureCargoOptionsForScope('cand');
    if (!dados) {
      await waitMinimumLoading(loadingStartedAt, 520);
      renderCandidateEmptyState('not-found');
      return;
    }

    if (!validateScopeFields('cand')) {
      await waitMinimumLoading(loadingStartedAt, 520);
      renderCandidateEmptyState();
      return;
    }

    clearScopeFeedback('cand');
    await waitMinimumLoading(loadingStartedAt, 520);
    await renderCandidateSearch(dados, q, getSelectedCargoKey('cand'));
  } finally {
    clearResultsPanelLoading('pn-results-cand');
  }
}

function initLogout() {
  const logoutLink = document.getElementById('pn-btn-logout');
  if (!logoutLink || logoutLink.dataset.publicNav === 'true') return;

  logoutLink.addEventListener('click', (e) => {
    e.preventDefault();
    AUTH.logout();
  });
}

function initFeedbackResetHandlers() {
  [
    'pn-sel-eleicao-mun', 'pn-sel-municipio-mun', 'pn-sel-cargo-mun',
    'pn-sel-eleicao-zona', 'pn-sel-municipio-zona', 'pn-sel-zona', 'pn-sel-cargo-zona',
    'pn-sel-eleicao-cand', 'pn-sel-municipio-cand', 'pn-sel-cargo-cand',
    'pn-sel-eleicao-comp', 'pn-sel-municipio-comp', 'pn-sel-cargo-comp'
  ].forEach(id => {
    document.getElementById(id)?.addEventListener('change', () => {
      clearFieldInvalid(id);
      const scope = id.includes('-zona') ? 'zona'
        : id.includes('-cand') ? 'cand'
          : id.includes('-comp') ? 'comp'
            : 'mun';
      clearScopeFeedback(scope);
    });

    document.getElementById(id)?.addEventListener('input', () => {
      clearFieldInvalid(id);
    });
  });
}

function validateScopeFields(scope) {
  const missing = getMissingScopeFields(scope);
  if (!missing.length) {
    clearScopeFeedback(scope);
    return true;
  }

  const message = scope === 'zona'
    ? 'Selecione eleição, município, zona e cargo para analisar.'
    : scope === 'comp'
      ? 'Selecione eleição, município e cargo para criar o comparativo.'
      : scope === 'cand'
        ? 'Selecione eleição, município e cargo para buscar candidato.'
        : 'Selecione eleição, município e cargo para analisar.';

  showInlineFeedback(scope, message, missing);
  focusFirstMissingField(missing);
  return false;
}

function getMissingScopeFields(scope) {
  return getScopeFields(scope)
    .filter(field => !String(document.getElementById(field.id)?.value || '').trim());
}

function showInlineFeedback(scope, message, fields = []) {
  const feedback = document.getElementById(`pn-feedback-${scope}`);
  if (!feedback) {
    showToast(message, 'warning');
    return;
  }

  feedback.textContent = message;
  feedback.hidden = false;

  fields.forEach(field => markFieldInvalid(field.id, message));
}

function clearScopeFeedback(scope) {
  const feedback = document.getElementById(`pn-feedback-${scope}`);
  if (feedback) {
    feedback.textContent = '';
    feedback.hidden = true;
  }

  getScopeFields(scope).forEach(field => clearFieldInvalid(field.id));
}

function markFieldInvalid(id, message) {
  const field = document.getElementById(id);
  if (!field) return;
  field.setAttribute('aria-invalid', 'true');
  field.setAttribute('aria-errormessage', getFeedbackIdForField(id));
  field.closest('.pn-card')?.classList.add('has-error');

  const trigger = field.closest('.pn-select-wrapper')?.querySelector('.pn-custom-select-trigger');
  if (trigger) {
    trigger.setAttribute('aria-invalid', 'true');
    trigger.setAttribute('aria-errormessage', getFeedbackIdForField(id));
  }
}

function clearFieldInvalid(id) {
  const field = document.getElementById(id);
  if (!field) return;
  field.removeAttribute('aria-invalid');
  field.removeAttribute('aria-errormessage');
  field.closest('.pn-card')?.classList.remove('has-error');

  const trigger = field.closest('.pn-select-wrapper')?.querySelector('.pn-custom-select-trigger');
  if (trigger) {
    trigger.removeAttribute('aria-invalid');
    trigger.removeAttribute('aria-errormessage');
  }
}

function getFeedbackIdForField(id) {
  if (id.includes('-zona')) return 'pn-feedback-zona';
  if (id.includes('-cand')) return 'pn-feedback-cand';
  if (id.includes('-comp')) return 'pn-feedback-comp';
  return 'pn-feedback-mun';
}

function getScopeFields(scope) {
  const fieldsByScope = {
    mun: [
      { id: 'pn-sel-municipio-mun', label: 'município' },
      { id: 'pn-sel-eleicao-mun', label: 'eleição' },
      { id: 'pn-sel-cargo-mun', label: 'cargo' },
    ],
    zona: [
      { id: 'pn-sel-municipio-zona', label: 'município' },
      { id: 'pn-sel-eleicao-zona', label: 'eleição' },
      { id: 'pn-sel-zona', label: 'zona' },
      { id: 'pn-sel-cargo-zona', label: 'cargo' },
    ],
    cand: [
      { id: 'pn-sel-municipio-cand', label: 'município' },
      { id: 'pn-sel-eleicao-cand', label: 'eleição' },
      { id: 'pn-sel-cargo-cand', label: 'cargo' },
    ],
    comp: [
      { id: 'pn-sel-municipio-comp', label: 'município' },
      { id: 'pn-sel-eleicao-comp', label: 'eleição' },
      { id: 'pn-sel-cargo-comp', label: 'cargo' },
    ],
  };

  return fieldsByScope[scope] || [];
}

function focusFirstMissingField(fields) {
  const first = fields[0];
  if (!first) return;
  const field = document.getElementById(first.id);
  const customTrigger = field?.closest('.pn-select-wrapper')?.querySelector('.pn-custom-select-trigger');
  const target = customTrigger || field;
  if (target && typeof target.focus === 'function') target.focus();
}

function showToast(message, type = 'info') {
  let stack = document.getElementById('pn-toast-stack');
  if (!stack) {
    stack = document.createElement('div');
    stack.id = 'pn-toast-stack';
    stack.className = 'pn-toast-stack';
    stack.setAttribute('aria-live', 'polite');
    stack.setAttribute('aria-atomic', 'true');
    document.body.appendChild(stack);
  }

  const toast = document.createElement('div');
  toast.className = `pn-toast ${type}`;
  toast.setAttribute('role', type === 'error' ? 'alert' : 'status');
  toast.textContent = message;
  stack.appendChild(toast);

  const maxVisibleToasts = 3;
  Array.from(stack.querySelectorAll('.pn-toast'))
    .slice(0, -maxVisibleToasts)
    .forEach(oldToast => oldToast.remove());

  const mobileToast = window.matchMedia('(max-width: 768px)').matches;
  const duration = type === 'error' || type === 'warning' ? 5000 : (mobileToast ? 2200 : 3600);
  window.setTimeout(() => {
    toast.classList.add('leaving');
    window.setTimeout(() => toast.remove(), 220);
  }, duration);
}

// ==========================================
// LOADING STATES
// ==========================================
function setButtonLoading(button, message) {
  if (!button) return;
  button.classList.add('is-loading');
  button.setAttribute('aria-busy', 'true');
  button.disabled = true;
  button.innerHTML = `<span class="pn-spinner" style="width:18px;height:18px;margin:0"></span><span>${esc(message)}</span>`;
}

function resetButton(button, label) {
  if (!button) return;
  const config = typeof label === 'string' ? { text: label, icon: 'chart' } : label;
  button.classList.remove('is-loading');
  button.removeAttribute('aria-busy');
  button.disabled = false;
  button.innerHTML = buttonInnerHtml(config.text, config.icon);
}

function setComparativoButtonLoading(button, message = 'Montando comparativo...') {
  if (!button) return;
  button.classList.add('loading', 'is-loading');
  button.setAttribute('aria-busy', 'true');
  button.disabled = true;
  button.innerHTML = `
    <span class="pn-spinner" aria-hidden="true"></span>
    <div class="pn-comp-text">
      <strong>${esc(message)}</strong>
      <span>Organizando candidatos, votos e diferenças</span>
    </div>
  `;
}

function resetComparativoButton(button) {
  if (!button) return;
  button.classList.remove('loading', 'is-loading');
  button.removeAttribute('aria-busy');
  button.disabled = false;
  button.innerHTML = `
    <div class="pn-comp-plus">+</div>
    <div class="pn-comp-text">
      <strong>Novo Comparativo</strong>
      <span>Selecione candidatos para comparar lado a lado</span>
    </div>
  `;
}

function renderAnalysisSkeleton(scope) {
  const prefix = scope === 'zona' ? 'zona' : 'mun';
  const results = document.getElementById(`pn-results-${prefix}`);
  if (!results) return;

  clearAnalysisEmptyState(prefix);
  results.classList.add('visible', 'is-loading');
  results.setAttribute('aria-busy', 'true');
  results.dataset.skeletonSection = prefix === 'zona' ? 'zona' : 'municipio';

  const cargosEl = document.getElementById(`pn-cargos-${prefix}`);
  if (cargosEl) cargosEl.innerHTML = '<span class="pn-loading-label">Carregando recorte</span>';

  const summaryEl = document.getElementById(`pn-summary-${prefix}`);
  if (summaryEl) {
    summaryEl.hidden = false;
    summaryEl.innerHTML = `
      <div class="pn-skeleton pn-skeleton-line" aria-hidden="true"></div>
      <div class="pn-summary-grid" aria-hidden="true">
        ${Array.from({ length: 4 }, () => '<div class="pn-skeleton pn-skeleton-kpi"></div>').join('')}
      </div>
    `;
  }

  const kpisEl = document.getElementById(`pn-kpis-${prefix}`);
  if (kpisEl) {
    kpisEl.innerHTML = Array.from({ length: 4 }, () =>
      '<div class="pn-skeleton pn-skeleton-kpi pn-skeleton-kpi-card" aria-hidden="true"></div>'
    ).join('');
  }

  const territoriesEl = document.getElementById(`pn-territorios-${prefix}`);
  if (territoriesEl) {
    territoriesEl.innerHTML = `
      <div class="pn-skeleton-panel" aria-hidden="true">
        <span class="pn-loading-label">Detectando redutos e oportunidades</span>
        <div class="pn-skeleton-comparison-grid">
          <div class="pn-skeleton-comparison-card">
            <div class="pn-skeleton pn-skeleton-line"></div>
            <div class="pn-skeleton pn-skeleton-row"></div>
          </div>
          <div class="pn-skeleton-comparison-card">
            <div class="pn-skeleton pn-skeleton-line"></div>
            <div class="pn-skeleton pn-skeleton-row"></div>
          </div>
        </div>
      </div>
    `;
  }

  const rankingEl = document.getElementById(`pn-ranking-${prefix}`);
  if (rankingEl) {
    rankingEl.innerHTML = Array.from({ length: 5 }, () =>
      '<div class="pn-skeleton pn-skeleton-row pn-skeleton-ranking-row" aria-hidden="true"></div>'
    ).join('');
  }

  const strengthEl = document.getElementById(`pn-forca-politica-${prefix}`);
  if (strengthEl) {
    strengthEl.innerHTML = `
      <div class="pn-chart-title"><div class="pn-ct-bar pn-ct-bar--strength"></div>FORÇA POLÍTICA</div>
      <div class="pn-skeleton pn-skeleton-chart" aria-hidden="true"></div>
    `;
  }

  if (prefix === 'mun') {
    setProfileChartsExpanded(true);
    destroyChart('genero');
    destroyChart('idade');
    destroyChart('instrucao');
    ['genero', 'idade', 'instrucao'].forEach(name => {
      renderProfileChartSkeleton(name, name === 'genero' ? 'donut' : 'bars');
      const summary = document.getElementById(`pn-summary-${name}`);
      if (summary) {
        summary.innerHTML = Array.from({ length: 3 }, () =>
          '<div class="pn-skeleton pn-skeleton-line" aria-hidden="true"></div>'
        ).join('');
      }
    });
  }
}

function renderProfileChartSkeleton(name, type) {
  const body = document.getElementById(`pn-profile-body-${name}`);
  const canvas = document.getElementById(`pn-chart-${name}`);
  const summary = document.getElementById(`pn-summary-${name}`);
  if (!body || !summary) return;
  body.querySelector('.pn-profile-skeleton')?.remove();
  if (canvas) canvas.hidden = true;
  const skeleton = document.createElement('div');
  skeleton.className = `pn-profile-skeleton pn-skeleton pn-skeleton-chart-${type}`;
  skeleton.setAttribute('aria-hidden', 'true');
  body.insertBefore(skeleton, summary);
}

function clearProfileChartSkeleton(name) {
  const body = document.getElementById(`pn-profile-body-${name}`);
  const canvas = document.getElementById(`pn-chart-${name}`);
  body?.querySelector('.pn-profile-skeleton')?.remove();
  if (canvas) canvas.hidden = false;
}

function renderCandidateSkeleton() {
  const container = document.getElementById('pn-results-cand');
  if (!container) return;
  container.classList.add('is-loading');
  container.setAttribute('aria-busy', 'true');
  container.dataset.skeletonSection = 'candidato';
  container.innerHTML = `
    <div class="pn-skeleton-panel" data-loading-section="candidato">
      <span class="pn-loading-label">Carregando candidatos</span>
      <div class="pn-skeleton-candidate-list" aria-hidden="true">
        ${Array.from({ length: 3 }, () => `
          <div class="pn-skeleton-candidate-card">
            <div class="pn-skeleton pn-skeleton-avatar"></div>
            <div class="pn-skeleton-candidate-lines">
              <div class="pn-skeleton pn-skeleton-line"></div>
              <div class="pn-skeleton pn-skeleton-line short"></div>
            </div>
            <div class="pn-skeleton pn-skeleton-votes"></div>
          </div>
        `).join('')}
      </div>
    </div>
  `;
}

function renderComparativoSkeleton(mode = 'candidatos') {
  const container = document.getElementById('pn-results-comp');
  if (!container) return;
  const isTemporal = mode === 'eleicoes';
  const label = isTemporal ? 'Comparando eleições' : 'Preparando comparativo';
  const steps = isTemporal
    ? ['Carregando eleições', 'Calculando variação partidária', 'Montando leitura do período']
    : ['Carregando votos do recorte', 'Organizando candidatos', 'Calculando líderes e diferenças'];
  container.classList.add('is-loading');
  container.setAttribute('aria-busy', 'true');
  container.dataset.skeletonSection = isTemporal ? 'comparativo-eleicoes' : 'comparativo';
  container.innerHTML = `
    <div class="pn-skeleton-panel" data-loading-section="${isTemporal ? 'comparativo-eleicoes' : 'comparativo'}" aria-live="polite">
      <span class="pn-loading-label">${esc(label)}</span>
      <div class="pn-skeleton-loading-steps" aria-hidden="true">
        ${steps.map(step => `<span class="pn-skeleton-loading-step">${esc(step)}</span>`).join('')}
      </div>
      <div class="pn-skeleton-comparison-grid" aria-hidden="true">
        ${Array.from({ length: 2 }, () => `
          <div class="pn-skeleton-comparison-card">
            <div class="pn-skeleton pn-skeleton-line"></div>
            <div class="pn-skeleton pn-skeleton-line short"></div>
            <div class="pn-skeleton pn-skeleton-kpi"></div>
          </div>
        `).join('')}
      </div>
      <div class="pn-skeleton-table" aria-hidden="true">
        ${Array.from({ length: 4 }, () => '<div class="pn-skeleton pn-skeleton-row pn-skeleton-ranking-row"></div>').join('')}
      </div>
    </div>
  `;
}

function clearResultsPanelLoading(id) {
  const container = document.getElementById(id);
  if (!container) return;
  container.classList.remove('is-loading');
  container.setAttribute('aria-busy', 'false');
  delete container.dataset.skeletonSection;
}

function clearAnalysisLoading(scope, options = {}) {
  const prefix = scope === 'zona' ? 'zona' : 'mun';
  const results = document.getElementById(`pn-results-${prefix}`);
  if (!results) return;
  results.classList.remove('is-loading');
  results.setAttribute('aria-busy', 'false');

  if (options.clear) {
    document.getElementById(`pn-cargos-${prefix}`)?.replaceChildren();
    const summaryEl = document.getElementById(`pn-summary-${prefix}`);
    if (summaryEl) {
      summaryEl.replaceChildren();
      summaryEl.hidden = true;
    }
    document.getElementById(`pn-kpis-${prefix}`)?.replaceChildren();
    document.getElementById(`pn-competitividade-${prefix}`)?.replaceChildren();
    document.getElementById(`pn-territorios-${prefix}`)?.replaceChildren();
    document.getElementById(`pn-ranking-${prefix}`)?.replaceChildren();
    document.getElementById(`pn-forca-politica-${prefix}`)?.replaceChildren();
    if (prefix === 'mun') {
      ['genero', 'idade', 'instrucao'].forEach(name => {
        clearProfileChartSkeleton(name);
        document.getElementById(`pn-summary-${name}`)?.replaceChildren();
      });
    }
  }
}

// ==========================================
// ANÁLISE POR MUNICÍPIO
// ==========================================
async function analisarMunicipio(municipio, ano, options = {}) {
  const btn = document.getElementById('pn-btn-analisar-mun');
  const manageLoading = options.showLoading !== false;
  const loadingStartedAt = Date.now();
  let loadingSettled = !manageLoading;
  const settleLoading = async () => {
    if (loadingSettled) return;
    await waitMinimumLoading(loadingStartedAt);
    loadingSettled = true;
  };
  if (manageLoading) {
    setButtonLoading(btn, 'Carregando dados do município...');
    renderAnalysisSkeleton('mun');
  }

  try {
    const agregado = await fetchAgregado(municipio, ano, { pageSize: 15 });
    let dados = null;
    let cargos = Array.isArray(agregado?.cargos_disponiveis) ? agregado.cargos_disponiveis : [];
    if (!agregado) {
      dados = await fetchDados(municipio, ano);
      if (!dados) {
        await settleLoading();
        if (manageLoading) clearAnalysisLoading('mun', { clear: true });
        return;
      }
      cargos = extractCargoKeys(dados);
    }
    if (!cargos.length) {
      await settleLoading();
      renderAnalysisEmptyState('mun', 'no-data');
      return;
    }

    STATE.municipioAnalise = {
      dados,
      agregado,
      municipio,
      ano,
      cargos,
      cargoKey: chooseDefaultCargoKey(cargos, options.cargoKey || getSelectedCargoKey('mun') || STATE.municipioAnalise?.cargoKey)
    };

    await settleLoading();
    renderMunicipioAnalise();

    document.getElementById('pn-results-mun').classList.add('visible');
    setMunicipioAnalysisCompact(true);

    if (options.save !== false) {
      const meta = getCargoMeta(STATE.municipioAnalise.cargoKey);
      const exportAgg = getMunicipioAnalysisAggregate();
      saveAnalysis('municipio', {
        uf: getCurrentUfForScope('mun', municipio),
        municipio,
        ano,
        cargo: meta.label,
        cargoKey: STATE.municipioAnalise.cargoKey,
        electionDataDate: ano,
        profileCharts: buildProfileChartExportData(exportAgg),
        label: `${municipio} — ${ano} — ${meta.label}`
      });
    }
  } finally {
    if (manageLoading) {
      await settleLoading();
      resetButton(btn, PANEL_BUTTON_LABELS.mun);
      clearAnalysisLoading('mun');
    }
  }
}

function renderMunicipioAnalise() {
  if (!STATE.municipioAnalise) return;
  clearAnalysisEmptyState('mun');

  const { cargos, cargoKey, municipio, ano } = STATE.municipioAnalise;
  const meta = getCargoMeta(cargoKey);
  const agg = getMunicipioAnalysisAggregate();
  if (!agg) return;
  const abstencoesKpi = formatAbstencoesKpi(agg);
  const votosKpi = formatVotesKpiForAggregate(meta, cargoKey, 'Cargo selecionado', agg);
  const brancosNulosKpi = formatBrancosNulosKpi(agg);

  // KPIs
  renderCargoTabs(document.getElementById('pn-cargos-mun'), cargos, cargoKey, 'municipio');
  syncCargoSelect('mun', cargos, cargoKey);
  renderExecutiveSummary(document.getElementById('pn-summary-mun'), agg, {
    scope: 'mun',
    titulo: `${municipio} · ${formatElectionLabel(ano)}`,
    cargoLabel: meta.label,
  });
  const kpisEl = document.getElementById('pn-kpis-mun');
  kpisEl.innerHTML = `
    ${kpiCard(formatNum(agg.totalEleitores), 'Eleitores', '', PANEL_COLORS.info)}
    ${kpiCard(formatNum(agg.totalSecoes), 'Seções', '', PANEL_COLORS.neutral)}
    ${kpiCard(formatNum(agg.totalVotosValidos), votosKpi.label, votosKpi.sub, PANEL_COLORS.success)}
    ${kpiCard(abstencoesKpi.value, 'Abstenções', abstencoesKpi.sub, PANEL_COLORS.danger)}
    ${brancosNulosKpi ? kpiCard(brancosNulosKpi.value, 'Brancos/Nulos', brancosNulosKpi.sub, PANEL_COLORS.warning) : ''}
  `;
  renderEleitoresNote(kpisEl, agg, 'municipio');
  renderCompetitiveness(document.getElementById('pn-competitividade-mun'), agg, {
    scope: 'municipio',
    sections: STATE.municipioAnalise.dados
      ? buildSectionCompetitivenessData(STATE.municipioAnalise.dados, {
        cargoKey,
        partidosCatalog: STATE.partidosCatalog,
      })
      : null,
  });
  renderTerritoryOpportunities(document.getElementById('pn-territorios-mun'), agg, {
    scope: 'municipio',
    dados: STATE.municipioAnalise.dados,
    cargoKey,
    partidosCatalog: STATE.partidosCatalog,
  });

  // Ranking
  renderRanking(document.getElementById('pn-ranking-mun'), agg.candidatos, { scope: 'municipio' });
  renderPoliticalStrength(document.getElementById('pn-forca-politica-mun'), agg);

  // Charts
  renderGeneroChart(agg.genero);
  renderIdadeChart(agg.idade);
  renderInstrucaoChart(agg.instrucao);
}

function getMunicipioAnalysisAggregate() {
  if (!STATE.municipioAnalise) return null;
  const { dados, agregado, cargoKey } = STATE.municipioAnalise;
  return serverAggregateToPanelAggregate(agregado, cargoKey)
    || (dados ? aggregateAll(dados, { cargoKey }) : null);
}

// ==========================================
// ANÁLISE POR ZONA
// ==========================================
async function analisarZona(municipio, ano, zona, options = {}) {
  const btn = document.getElementById('pn-btn-analisar-zona');
  const manageLoading = options.showLoading !== false;
  const loadingStartedAt = Date.now();
  let loadingSettled = !manageLoading;
  const settleLoading = async () => {
    if (loadingSettled) return;
    await waitMinimumLoading(loadingStartedAt);
    loadingSettled = true;
  };
  if (manageLoading) {
    setButtonLoading(btn, 'Carregando dados da zona...');
    renderAnalysisSkeleton('zona');
  }

  try {
    const agregado = await fetchAgregado(municipio, ano, { scope: 'zona', zona, pageSize: 15 });
    let dados = null;
    let cargos = Array.isArray(agregado?.cargos_disponiveis) ? agregado.cargos_disponiveis : [];
    if (!agregado) {
      dados = await fetchDados(municipio, ano);
      if (!dados) {
        await settleLoading();
        if (manageLoading) clearAnalysisLoading('zona', { clear: true });
        return;
      }
      cargos = extractCargoKeys(dados);
    }
    if (!cargos.length) {
      await settleLoading();
      renderAnalysisEmptyState('zona', 'no-data');
      return;
    }

    STATE.zonaAnalise = {
      dados,
      agregado,
      municipio,
      ano,
      zona,
      cargos,
      cargoKey: chooseDefaultCargoKey(cargos, options.cargoKey || getSelectedCargoKey('zona') || STATE.zonaAnalise?.cargoKey)
    };

    await settleLoading();
    renderZonaAnalise();
    document.getElementById('pn-results-zona').classList.add('visible');

    if (options.save !== false) {
      const meta = getCargoMeta(STATE.zonaAnalise.cargoKey);
      saveAnalysis('zona', {
        uf: getCurrentUfForScope('zona', municipio),
        municipio,
        ano,
        zona,
        cargo: meta.label,
        cargoKey: STATE.zonaAnalise.cargoKey,
        label: `Zona ${zona} — ${municipio} — ${ano} — ${meta.label}`
      });
    }
  } finally {
    if (manageLoading) {
      await settleLoading();
      resetButton(btn, PANEL_BUTTON_LABELS.zona);
      clearAnalysisLoading('zona');
    }
  }
}

function renderZonaAnalise() {
  if (!STATE.zonaAnalise) return;
  clearAnalysisEmptyState('zona');

  const { zona, cargos, cargoKey, municipio, ano } = STATE.zonaAnalise;
  const meta = getCargoMeta(cargoKey);
  const agg = getZonaAnalysisAggregate();
  if (!agg) return;
  const abstencoesKpi = formatAbstencoesKpi(agg);
  const votosKpi = formatVotesKpiForAggregate(meta, cargoKey, 'Cargo selecionado', agg);
  const brancosNulosKpi = formatBrancosNulosKpi(agg);

  const kpisEl = document.getElementById('pn-kpis-zona');
  renderCargoTabs(document.getElementById('pn-cargos-zona'), cargos, cargoKey, 'zona');
  syncCargoSelect('zona', cargos, cargoKey);
  renderExecutiveSummary(document.getElementById('pn-summary-zona'), agg, {
    scope: 'zona',
    titulo: `Zona ${zona} · ${municipio} · ${formatElectionLabel(ano)}`,
    cargoLabel: meta.label,
  });
  kpisEl.innerHTML = `
    ${kpiCard(formatNum(agg.totalEleitores), 'Eleitores', `Zona ${zona}`, PANEL_COLORS.info)}
    ${kpiCard(formatNum(agg.totalSecoes), 'Seções', '', PANEL_COLORS.neutral)}
    ${kpiCard(formatNum(agg.totalVotosValidos), votosKpi.label, votosKpi.sub, PANEL_COLORS.success)}
    ${kpiCard(abstencoesKpi.value, 'Abstenções', abstencoesKpi.sub, PANEL_COLORS.danger)}
    ${brancosNulosKpi ? kpiCard(brancosNulosKpi.value, 'Brancos/Nulos', brancosNulosKpi.sub, PANEL_COLORS.warning) : ''}
  `;
  renderEleitoresNote(kpisEl, agg, 'zona');
  renderCompetitiveness(document.getElementById('pn-competitividade-zona'), agg, {
    scope: 'zona',
    zona,
    sections: STATE.zonaAnalise.dados
      ? buildSectionCompetitivenessData(STATE.zonaAnalise.dados, {
        cargoKey,
        zona,
        partidosCatalog: STATE.partidosCatalog,
      })
      : null,
  });
  renderTerritoryOpportunities(document.getElementById('pn-territorios-zona'), agg, {
    scope: 'zona',
    zona,
    dados: STATE.zonaAnalise.dados,
    cargoKey,
    partidosCatalog: STATE.partidosCatalog,
  });

  renderRanking(document.getElementById('pn-ranking-zona'), agg.candidatos, { scope: 'zona' });
  renderPoliticalStrength(document.getElementById('pn-forca-politica-zona'), agg);
}

function getZonaAnalysisAggregate() {
  if (!STATE.zonaAnalise) return null;
  const { dados, agregado, cargoKey, zona } = STATE.zonaAnalise;
  return serverAggregateToPanelAggregate(agregado, cargoKey)
    || (dados ? aggregateByZona(dados, zona, { cargoKey }) : null);
}

function handlePanelCargoClick(e) {
  const loadMoreBtn = e.target.closest('[data-ranking-load-more]');
  if (loadMoreBtn) {
    void handleRankingLoadMore(loadMoreBtn);
    return;
  }

  const btn = e.target.closest('[data-panel-cargo]');
  if (!btn) return;

  const cargoKey = btn.dataset.panelCargo;
  const scope = btn.dataset.panelScope;
  if (!cargoKey || !scope) return;

  if (scope === 'municipio' && STATE.municipioAnalise) {
    STATE.municipioAnalise.cargoKey = cargoKey;
    renderMunicipioAnalise();
  }

  if (scope === 'zona' && STATE.zonaAnalise) {
    STATE.zonaAnalise.cargoKey = cargoKey;
    renderZonaAnalise();
  }
}

// ==========================================
// ANÁLISE DE COMPARATIVO
// ==========================================
async function handleRankingLoadMore(button) {
  const scope = button.dataset.rankingLoadMore;
  const stateKey = scope === 'zona' ? 'zonaAnalise' : 'municipioAnalise';
  const current = STATE[stateKey];
  if (!current?.agregado || !current.cargoKey) return;

  const cargoData = current.agregado?.por_cargo?.[current.cargoKey];
  const ranking = cargoData?.ranking || {};
  if (!ranking.has_more && !ranking.hasMore) return;

  const previousLabel = button.textContent;
  button.disabled = true;
  button.textContent = 'Carregando...';

  try {
    const nextPage = Number(ranking.page || 1) + 1;
    const nextAggregate = await fetchAgregado(current.municipio, current.ano, {
      cargoKey: current.cargoKey,
      scope: scope === 'zona' ? 'zona' : 'municipio',
      zona: scope === 'zona' ? current.zona : '',
      page: nextPage,
      pageSize: Number(ranking.page_size || ranking.pageSize || 15),
    });
    if (!nextAggregate) {
      button.disabled = false;
      button.textContent = previousLabel || 'Carregar mais';
      showToast('Não foi possível carregar mais candidatos agora.', 'error');
      return;
    }

    current.agregado = mergeServerAggregatePage(current.agregado, nextAggregate, current.cargoKey);
    if (scope === 'zona') {
      renderZonaAnalise();
    } else {
      renderMunicipioAnalise();
    }
  } catch (err) {
    console.warn('[Painel] Falha ao carregar ranking paginado:', err);
    button.disabled = false;
    button.textContent = previousLabel || 'Carregar mais';
    showToast('Não foi possível carregar mais candidatos agora.', 'error');
  }
}

function mergeServerAggregatePage(currentAggregate, nextAggregate, cargoKey) {
  const currentCargo = currentAggregate?.por_cargo?.[cargoKey];
  const nextCargo = nextAggregate?.por_cargo?.[cargoKey];
  if (!currentCargo || !nextCargo) return currentAggregate || nextAggregate;

  const currentItems = Array.isArray(currentCargo.ranking?.items) ? currentCargo.ranking.items : [];
  const nextItems = Array.isArray(nextCargo.ranking?.items) ? nextCargo.ranking.items : [];
  const seen = new Set(currentItems.map(item => item.id || `${item.cargoKey || cargoKey}_${item.numero}`));
  const mergedItems = [
    ...currentItems,
    ...nextItems.filter(item => {
      const key = item.id || `${item.cargoKey || cargoKey}_${item.numero}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
  ];

  return {
    ...currentAggregate,
    por_cargo: {
      ...currentAggregate.por_cargo,
      [cargoKey]: {
        ...currentCargo,
        ranking: {
          ...currentCargo.ranking,
          ...nextCargo.ranking,
          items: mergedItems,
        }
      }
    }
  };
}

function setComparativoMode(mode = 'candidatos') {
  const normalized = mode === 'eleicoes' ? 'eleicoes' : 'candidatos';
  STATE.comparativoModo = normalized;

  document.querySelectorAll('[data-comp-mode]').forEach(button => {
    button.classList.toggle('active', button.dataset.compMode === normalized);
    button.setAttribute('aria-pressed', button.dataset.compMode === normalized ? 'true' : 'false');
  });

  const electionControls = document.getElementById('pn-election-compare-controls');
  const candidateButton = document.getElementById('pn-btn-novo-comp');
  if (electionControls) electionControls.hidden = normalized !== 'eleicoes';
  if (candidateButton) candidateButton.hidden = normalized === 'eleicoes';

  resetComparativo();
  if (normalized === 'eleicoes') refreshElectionPairOptions();
}

function refreshElectionPairOptions() {
  const select = document.getElementById('pn-sel-comp-election-pair');
  if (!select) return;

  const recorte = STATE.recorte || getGlobalRecorte();
  if (recorte.complete) syncScopeWithGlobal('comp', recorte);
  const values = getScopeValues('comp');
  const municipio = recorte.complete ? recorte.municipio : values.municipio;
  const uf = recorte.complete ? recorte.uf : values.uf;
  const cargoKey = recorte.complete ? recorte.cargoKey : getSelectedCargoKey('comp');
  const eleicoes = getEleicoesDisponiveisMunicipio(uf, municipio);
  const pairs = buildElectionPairOptions(eleicoes, { cargoKey });
  const pairModel = buildElectionPairSelectModel(pairs);
  const previous = select.value;

  select.innerHTML = '<option value="">Selecione duas eleições</option>';
  pairModel.groups.forEach(group => {
    const optgroup = document.createElement('optgroup');
    optgroup.label = group.label;
    select.appendChild(optgroup);

    group.pairs.forEach(pair => {
      const opt = document.createElement('option');
      opt.value = `${pair.from}|${pair.to}`;
      opt.textContent = formatElectionPairLabel(pair);
      opt.disabled = pair.enabled === false;
      opt.dataset.group = group.label;
      opt.dataset.reason = pair.reason || '';
      if (pair.recommended) opt.dataset.recommended = 'true';
      optgroup.appendChild(opt);
    });
  });

  if (previous && pairModel.visiblePairs.some(pair => `${pair.from}|${pair.to}` === previous)) {
    select.value = previous;
  }
  renderElectionPairNote(pairModel);
  syncCustomSelect(select);
}

function renderElectionPairNote(model) {
  const note = document.getElementById('pn-election-pair-note');
  if (!note) return;
  note.replaceChildren();

  const primary = document.createElement('span');
  primary.textContent = model.primaryNote;
  note.appendChild(primary);

  if (model.hiddenDetails) {
    const details = document.createElement('details');
    const summary = document.createElement('summary');
    const text = document.createElement('small');
    summary.textContent = model.hiddenDetailsSummary;
    text.textContent = model.hiddenDetails;
    details.append(summary, text);
    note.appendChild(details);
  }
}

function getEleicoesDisponiveisMunicipio(uf = '', municipio = '') {
  const key = uf && municipio ? `${uf}:${municipio}` : '';
  const catalog = STATE.catalogo || {};
  const byMunicipio = catalog.eleicoes_por_municipio || {};
  const direct = key ? byMunicipio[key] : null;
  const normalizedKey = key ? Object.keys(byMunicipio).find(k => normalizeText(k) === normalizeText(key)) : '';
  const source = direct || (normalizedKey ? byMunicipio[normalizedKey] : null) || catalog.eleicoes || AUTH.eleicoes || [];

  return [...new Set(source.map(getElectionValue).filter(Boolean))]
    .sort(compareElectionValues);
}

async function compararEleicoes() {
  setComparativoMode('eleicoes');
  const recorte = STATE.recorte || getGlobalRecorte();
  if (recorte.complete) syncScopeWithGlobal('comp', recorte);
  await ensureCargoOptionsForScope('comp');
  if (!validateScopeFields('comp')) return;

  const pairValue = document.getElementById('pn-sel-comp-election-pair')?.value || '';
  const [anoA, anoB] = pairValue.split('|');
  if (!anoA || !anoB) {
    showInlineFeedback('comp', 'Selecione duas eleições para comparar.');
    return;
  }

  const values = getScopeValues('comp');
  const municipio = recorte.complete ? recorte.municipio : values.municipio;
  const cargoKey = getSelectedCargoKey('comp');
  const btn = document.getElementById('pn-btn-comparar-eleicoes');
  const loadingStartedAt = Date.now();
  setButtonLoading(btn, 'Comparando eleições...');
  renderComparativoSkeleton('eleicoes');

  try {
    const [dadosA, dadosB] = await Promise.all([
      fetchDados(municipio, anoA),
      fetchDados(municipio, anoB)
    ]);

    await waitMinimumLoading(loadingStartedAt);
    if (!dadosA || !dadosB) {
      renderElectionComparisonEmptyState('no-data');
      return;
    }

    const comparison = buildElectionComparisonData({
      dadosA,
      dadosB,
      anoA,
      anoB,
      cargoKey
    });
    renderElectionComparison(comparison, { municipio });
  } finally {
    resetButton(btn, { text: 'Comparar eleições', icon: 'compare' });
    clearResultsPanelLoading('pn-results-comp');
  }
}

async function iniciarComparativo() {
  const recorte = STATE.recorte || getGlobalRecorte();
  if (recorte.complete) syncScopeWithGlobal('comp', recorte);
  const ano = recorte.complete ? recorte.ano : document.getElementById('pn-sel-eleicao-comp')?.value;
  const municipio = recorte.complete ? recorte.municipio : document.getElementById('pn-sel-municipio-comp')?.value;
  const btn = document.getElementById('pn-btn-novo-comp');

  await ensureCargoOptionsForScope('comp');
  if (!validateScopeFields('comp')) return;
  const loadingStartedAt = Date.now();
  setComparativoButtonLoading(btn);
  renderComparativoSkeleton();

  try {
    const dados = await fetchDados(municipio, ano);
    if (!dados) {
      await waitMinimumLoading(loadingStartedAt);
      renderComparativoEmptyState();
      return;
    }

    const cargos = extractCargoKeys(dados);
    if (!cargos.length) {
      const container = document.getElementById('pn-results-comp');
      await waitMinimumLoading(loadingStartedAt);
      if (container) {
        container.innerHTML = emptyStateHtml({
          scope: 'comparativo',
          icon: 'compare',
          title: 'Nenhum cargo com votos',
          text: 'A origem carregada não possui votos para montar comparativos neste recorte.',
          action: 'focus-global-context',
          actionLabel: 'Ajustar recorte'
        });
      }
      return;
    }

    STATE.comparativo = {
      dados,
      municipio,
      ano,
      cargos,
      cargoKey: chooseDefaultCargoKey(cargos, getSelectedCargoKey('comp')),
      selectedIds: [],
      query: ''
    };

    await waitMinimumLoading(loadingStartedAt);
    renderComparativo();
  } finally {
    resetComparativoButton(btn);
    clearResultsPanelLoading('pn-results-comp');
  }
}

function resetComparativo() {
  STATE.comparativo = null;
  const results = document.getElementById('pn-results-comp');
  if (results) renderComparativoEmptyState();
  renderSavedComparativos();
}

function renderCargoTabs(container, cargos, activeCargoKey, scope) {
  if (!container) return;
  if (!cargos.length) {
    container.innerHTML = '';
    return;
  }

  container.innerHTML = cargos.map(cargoKey => {
    const meta = getCargoMeta(cargoKey);
    return `
      <button type="button" class="pn-comp-cargo-btn ${cargoKey === activeCargoKey ? 'active' : ''}" data-panel-scope="${esc(scope)}" data-panel-cargo="${esc(cargoKey)}">
        ${esc(meta.label)}
      </button>
    `;
  }).join('');
}

function handleComparativoClick(e) {
  const quick = e.target.closest('[data-comp-quick]');
  if (quick && STATE.comparativo) {
    applyComparativoQuickAction(quick.dataset.compQuick);
    return;
  }

  const cargoBtn = e.target.closest('[data-comp-cargo]');
  if (cargoBtn && STATE.comparativo) {
    STATE.comparativo.cargoKey = cargoBtn.dataset.compCargo;
    STATE.comparativo.selectedIds = [];
    STATE.comparativo.query = '';
    renderComparativo();
    return;
  }

  const toggle = e.target.closest('[data-comp-toggle]');
  if (toggle && STATE.comparativo) {
    const id = toggle.dataset.compToggle;
    const selected = new Set(STATE.comparativo.selectedIds);
    if (selected.has(id)) {
      selected.delete(id);
    } else {
      if (selected.size >= 6) {
        showInlineFeedback('comp', 'Compare até 6 candidatos por vez para manter a leitura clara.');
        showToast('Limite de 6 candidatos no comparativo.', 'warning');
        return;
      }
      selected.add(id);
    }
    STATE.comparativo.selectedIds = Array.from(selected);
    renderComparativo();
    return;
  }

  const remove = e.target.closest('[data-comp-remove]');
  if (remove && STATE.comparativo) {
    STATE.comparativo.selectedIds = STATE.comparativo.selectedIds.filter(id => id !== remove.dataset.compRemove);
    renderComparativo();
    return;
  }

  const save = e.target.closest('[data-comp-save]');
  if (save) salvarComparativo();
}

function applyComparativoQuickAction(action) {
  if (!STATE.comparativo) return;

  const comp = buildComparativoData(STATE.comparativo.dados, STATE.comparativo.cargoKey, []);
  if (action === 'clear') {
    STATE.comparativo.selectedIds = [];
  } else if (action === 'leaders') {
    STATE.comparativo.selectedIds = comp.candidatos.slice(0, 2).map(c => c.id);
  } else if (action === 'top3') {
    STATE.comparativo.selectedIds = comp.candidatos.slice(0, 3).map(c => c.id);
  }

  STATE.comparativo.query = '';
  clearScopeFeedback('comp');
  renderComparativo();
}

function handleComparativoInput(e) {
  if (!STATE.comparativo || e.target.id !== 'pn-comp-search') return;
  STATE.comparativo.query = e.target.value;
  renderComparativo();
  const input = document.getElementById('pn-comp-search');
  if (input instanceof HTMLInputElement) {
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
  }
}

function renderComparativo() {
  const container = document.getElementById('pn-results-comp');
  if (!container || !STATE.comparativo) return;

  const { dados, municipio, ano, cargoKey, selectedIds, query } = STATE.comparativo;
  const meta = getCargoMeta(cargoKey);
  const comp = buildComparativoData(dados, cargoKey, selectedIds);
  syncCargoSelect('comp', STATE.comparativo.cargos, cargoKey);
  const selectedSet = new Set(comp.selecionados.map(c => c.id));
  const candidateList = buildCandidateListViewModel(comp.candidatos, query, { limit: 24 });
  const candidates = candidateList.items;

  container.innerHTML = `
    <div class="pn-comp-panel">
      <div class="pn-comp-head">
        <div>
          <div class="pn-comp-eyebrow">Comparativo de candidatos</div>
          <h3>${esc(municipio)} — ${esc(ano)}</h3>
        </div>
        <div class="pn-comp-meta">${formatNum(comp.totalSecoes)} seções analisadas · ${formatNum(comp.totalCargoVotos)} votos no cargo</div>
      </div>

      <div class="pn-comp-cargos" role="group" aria-label="Selecionar cargo">
        ${STATE.comparativo.cargos.map(c => `
          <button type="button" class="pn-comp-cargo-btn ${c === cargoKey ? 'active' : ''}" data-comp-cargo="${esc(c)}">
            ${esc(getCargoMeta(c).label)}
          </button>
        `).join('')}
      </div>

      <div class="pn-comp-quick-actions" role="group" aria-label="Ações rápidas do comparativo">
        <button type="button" data-comp-quick="leaders">Comparar líderes</button>
        <button type="button" data-comp-quick="top3">Top 3</button>
        <button type="button" data-comp-quick="clear">Limpar seleção</button>
      </div>

      <div class="pn-search-wrapper pn-comp-search">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/></svg>
        <input type="text" class="pn-search" id="pn-comp-search" value="${esc(query)}" placeholder="Buscar ${esc(meta.singular)} por nome, número ou partido...">
      </div>

      <div class="pn-comp-selected">
        ${comp.selecionados.length ? comp.selecionados.map(c => `
          <span class="pn-comp-chip">
            ${esc(c.nome)} · ${esc(c.numero)}
            <button type="button" data-comp-remove="${esc(c.id)}" title="Remover candidato">×</button>
          </span>
        `).join('') : '<span class="pn-comp-hint">Selecione pelo menos dois candidatos para gerar a comparação.</span>'}
      </div>

      <div class="pn-comp-candidate-grid">
        ${candidates.map(c => `
          <button type="button" class="pn-comp-candidate ${selectedSet.has(c.id) ? 'selected' : ''}" data-comp-toggle="${esc(c.id)}">
            <span class="pn-comp-candidate-main">
              <strong>${esc(c.nome)}</strong>
              <small>${esc(meta.label)} · Nº ${esc(c.numero)}${c.partido ? ' · ' + esc(c.partido) : ''}</small>
            </span>
            <span class="pn-comp-candidate-votes">
              ${formatNum(c.votos)}
              <small>${formatPercent(c.percentual)}</small>
            </span>
          </button>
        `).join('') || '<div class="pn-empty"><p>Nenhum candidato encontrado para a busca.</p></div>'}
      </div>
      ${candidateList.truncated ? `<div class="pn-list-note">Mostrando ${formatNum(candidates.length)} de ${formatNum(candidateList.total)} candidatos encontrados. Use a busca por nome, nÃºmero ou partido para localizar candidatos fora do topo.</div>` : ''}

      ${renderComparativoResultado(comp, meta)}
    </div>
  `;

  renderSavedComparativos();
}

function renderElectionComparison(comparison, context = {}) {
  const container = document.getElementById('pn-results-comp');
  if (!container) return;

  if (!comparison?.cargoKey) {
    container.innerHTML = `
      <div class="pn-comp-panel pn-election-comparison">
        <div class="pn-comp-placeholder">
          <strong>Nenhum cargo comparável</strong>
          <span>As eleições selecionadas não possuem o mesmo cargo carregado. Escolha outro par temporal ou ajuste o recorte.</span>
        </div>
      </div>
    `;
    return;
  }

  const meta = getCargoMeta(comparison.cargoKey);
  const leaderA = comparison.aggA.candidatos[0];
  const leaderB = comparison.aggB.candidatos[0];
  const dominantParty = comparison.partidosDelta[0];
  const deltaClass = comparison.votosDelta >= 0 ? 'positive' : 'negative';

  container.innerHTML = `
    <div class="pn-comp-panel pn-election-comparison">
      <div class="pn-comp-head">
        <div>
          <div class="pn-comp-eyebrow">Comparativo temporal</div>
          <h3>Comparação entre eleições</h3>
        </div>
        <div class="pn-comp-meta">
          ${esc(context.municipio || '')}<br>
          ${esc(formatElectionLabel(comparison.anoA))} x ${esc(formatElectionLabel(comparison.anoB))}
        </div>
      </div>

      <div class="pn-election-summary">
        <span>Cargo comparável</span>
        <strong>${esc(meta.label)}</strong>
        <small>${esc(formatElectionLabel(comparison.anoA))} → ${esc(formatElectionLabel(comparison.anoB))}</small>
      </div>

      <div class="pn-kpi-grid pn-comp-kpis">
        ${kpiCard(formatNum(comparison.aggA.totalVotosValidos), formatElectionLabel(comparison.anoA), leaderA ? `Líder: ${esc(leaderA.nome)}` : 'Sem líder identificado', PANEL_COLORS.neutral)}
        ${kpiCard(formatNum(comparison.aggB.totalVotosValidos), formatElectionLabel(comparison.anoB), leaderB ? `Líder: ${esc(leaderB.nome)}` : 'Sem líder identificado', PANEL_COLORS.info)}
        ${kpiCard(formatSignedNum(comparison.votosDelta), 'Variação de votos', `${esc(meta.label)} no recorte selecionado`, comparison.votosDelta >= 0 ? PANEL_COLORS.success : PANEL_COLORS.danger)}
        ${kpiCard(dominantParty ? esc(dominantParty.nome) : 'N/D', 'Maior variação partidária', dominantParty ? `${formatSignedNum(dominantParty.deltaVotos)} voto(s)` : 'Sem partido comparável', PANEL_COLORS.warning)}
      </div>

      <div class="pn-grid-2cols pn-comp-tables">
        <div class="pn-chart-card">
          <div class="pn-chart-title"><div class="pn-ct-bar pn-ct-bar--ranking"></div>Variação de votos por partido</div>
          ${renderPoliticalDeltaTable(comparison.partidosDelta, comparison.anoA, comparison.anoB)}
        </div>
        <div class="pn-chart-card">
          <div class="pn-chart-title"><div class="pn-ct-bar pn-ct-bar--decisive"></div>Leitura do período</div>
          <div class="pn-temporal-insights">
            <div>
              <span>Variação total</span>
              <strong class="${deltaClass}">${formatSignedNum(comparison.votosDelta)} votos</strong>
              <small>Diferença entre os votos computados do cargo nas duas eleições.</small>
            </div>
            <div>
              <span>Liderança</span>
              <strong>${esc(leaderB?.nome || 'N/D')}</strong>
              <small>${leaderB ? `${formatNum(leaderB.votos)} votos em ${formatElectionLabel(comparison.anoB)}` : 'Nenhum candidato com voto no período final.'}</small>
            </div>
            <div>
              <span>Partido em destaque</span>
              <strong>${esc(dominantParty?.nome || 'N/D')}</strong>
              <small>${dominantParty ? `${formatSignedNum(dominantParty.deltaVotos)} votos no intervalo` : 'Sem variação partidária disponível.'}</small>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

function renderPoliticalDeltaTable(rows, anoA, anoB) {
  if (!rows.length) return '<div class="pn-empty"><p>Nenhum partido comparável entre as eleições selecionadas.</p></div>';
  return `
    <div class="pn-table-scroll">
      <table class="pn-ranking pn-comp-table">
        <thead>
          <tr>
            <th>Partido</th>
            <th style="text-align:right">${esc(formatElectionLabel(anoA))}</th>
            <th style="text-align:right">${esc(formatElectionLabel(anoB))}</th>
            <th style="text-align:right">Delta</th>
          </tr>
        </thead>
        <tbody>
          ${rows.slice(0, 12).map(row => `
            <tr>
              <td class="pn-rank-name">
                <span class="pn-strength-group">
                  <span class="pn-strength-name">${esc(row.nome)}</span>
                  ${row.lineageNote ? `<small>${esc(row.lineageNote)}</small>` : ''}
                </span>
              </td>
              <td class="pn-rank-votes">${formatNum(row.votosA)}</td>
              <td class="pn-rank-votes">${formatNum(row.votosB)}</td>
              <td class="pn-rank-votes ${row.deltaVotos >= 0 ? 'is-positive' : 'is-negative'}">${formatSignedNum(row.deltaVotos)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>
  `;
}

function renderComparativoResultado(comp, meta) {
  if (comp.selecionados.length < 2) {
    return `
      <div class="pn-comp-placeholder">
        <strong>Comparativo aguardando seleção</strong>
        <span>Escolha 2 a 6 candidatos do cargo ${esc(meta.label)} para ver diferença, percentuais e distribuição por zona e seção.</span>
      </div>
    `;
  }

  const leader = comp.selecionados[0];
  const second = comp.selecionados[1];
  const votosKpi = formatVotesKpiForAggregate(meta, comp.cargoKey, 'Total do cargo no recorte', comp);

  return `
    <div class="pn-kpi-grid pn-comp-kpis">
      ${kpiCard(formatNum(leader.votos), `Líder: ${esc(leader.nome)}`, `${formatPercent(leader.percentual)} dos votos do cargo`, PANEL_COLORS.success)}
      ${kpiCard(formatNum(comp.diferencaPrimeiroSegundo), 'Diferença para o 2º', `${esc(second.nome)}: ${formatNum(second.votos)} votos`, PANEL_COLORS.warning)}
      ${kpiCard(formatNum(comp.totalCargoVotos), votosKpi.label, votosKpi.sub, PANEL_COLORS.success)}
      ${kpiCard(formatNum(comp.selecionados.length), 'Candidatos comparados', `${formatNum(comp.zonas.length)} zona(s) · ${formatNum(comp.secoes.length)} seção(ões) com votos`, PANEL_COLORS.neutral)}
    </div>

    <div class="pn-comp-card-grid">
      ${comp.selecionados.map(c => `
        <div class="pn-comp-result-card ${c.id === leader.id ? 'leader' : ''}">
          <div class="pn-comp-card-top">
            <div>
              <strong>${esc(c.nome)}</strong>
              <span>${esc(meta.label)} · Nº ${esc(c.numero)}</span>
            </div>
            <div class="pn-comp-rank">#${c.rank}</div>
          </div>
          <div class="pn-comp-vote">${formatNum(c.votos)}</div>
          <div class="pn-comp-progress"><span style="width:${Math.max(2, Math.min(100, c.percentual))}%"></span></div>
          <div class="pn-comp-card-stats">
            <span>${formatPercent(c.percentual)} do cargo</span>
            <span>${c.diffToLeader === 0 ? 'Líder' : `${formatNum(c.diffToLeader)} atrás`}</span>
            <span>${formatNum(c.zonasVencidas)} zona(s) vencida(s)</span>
            <span>${formatNum(c.secoesVencidas)} seção(ões) vencida(s)</span>
          </div>
        </div>
      `).join('')}
    </div>

    <div class="pn-comp-actions">
      <button type="button" class="pn-btn" data-comp-save>${buttonInnerHtml('Salvar Comparativo', 'save')}</button>
    </div>

    <div class="pn-grid-2cols pn-comp-tables">
      <div class="pn-chart-card">
      <div class="pn-chart-title"><div class="pn-ct-bar pn-ct-bar--ranking"></div>VOTOS POR ZONA</div>
        ${renderComparativoZonaTable(comp)}
      </div>
      <div class="pn-chart-card">
      <div class="pn-chart-title"><div class="pn-ct-bar pn-ct-bar--decisive"></div>SEÇÕES DECISIVAS</div>
        ${renderComparativoSecaoTable(comp)}
      </div>
    </div>
  `;
}

function renderComparativoZonaTable(comp) {
  if (!comp.zonas.length) return '<div class="pn-empty"><p>Nenhuma zona com votos para os candidatos selecionados.</p></div>';
  return `
    <div class="pn-table-scroll">
      <table class="pn-ranking pn-comp-table">
        <thead>
          <tr>
            <th>Zona</th>
            ${comp.selecionados.map(c => `<th style="text-align:right">${esc(c.nome)}</th>`).join('')}
            <th>Líder</th>
            <th style="text-align:right">Dif.</th>
          </tr>
        </thead>
        <tbody>
          ${comp.zonas.map(z => `
            <tr>
              <td class="pn-rank-name">Zona ${esc(z.zona)}</td>
              ${comp.selecionados.map(c => `<td class="pn-rank-votes">${formatNum(z.votosPorCandidato[c.id] || 0)}</td>`).join('')}
              <td>${esc(z.lider?.nome || 'N/D')}</td>
              <td class="pn-rank-votes">${formatNum(z.diferenca)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>
  `;
}

function renderComparativoSecaoTable(comp) {
  const secoes = comp.secoes.slice(0, 12);
  if (!secoes.length) return '<div class="pn-empty"><p>Nenhuma seção com votos para os candidatos selecionados.</p></div>';
  return `
    <div class="pn-table-scroll">
      <table class="pn-ranking pn-comp-table">
        <thead>
          <tr>
            <th>Seção</th>
            <th>Zona</th>
            <th>Líder</th>
            <th style="text-align:right">Dif.</th>
          </tr>
        </thead>
        <tbody>
          ${secoes.map(s => `
            <tr>
              <td class="pn-rank-name">${esc(s.secao)}</td>
              <td>${esc(s.zona)}</td>
              <td>${esc(s.lider?.nome || 'N/D')}</td>
              <td class="pn-rank-votes">${formatNum(s.diferenca)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>
  `;
}

function salvarComparativo() {
  if (!STATE.comparativo) return;
  const comp = buildComparativoData(STATE.comparativo.dados, STATE.comparativo.cargoKey, STATE.comparativo.selectedIds);
  if (comp.selecionados.length < 2) {
    showInlineFeedback('comp', 'Selecione pelo menos dois candidatos para salvar o comparativo.');
    return;
  }

  const meta = getCargoMeta(STATE.comparativo.cargoKey);
  const nomes = comp.selecionados.map(c => c.nome).slice(0, 3).join(' x ');
  const extra = comp.selecionados.length > 3 ? ` +${comp.selecionados.length - 3}` : '';
  saveAnalysis('comparativo', {
    municipio: STATE.comparativo.municipio,
    uf: getCurrentUfForScope('comp', STATE.comparativo.municipio),
    ano: STATE.comparativo.ano,
    cargo: meta.label,
    cargoKey: STATE.comparativo.cargoKey,
    candidatos: comp.selecionados.map(c => ({ id: c.id, nome: c.nome, numero: c.numero, votos: c.votos, percentual: c.percentual })),
    label: `Comparativo ${meta.label} — ${STATE.comparativo.municipio} — ${STATE.comparativo.ano}: ${nomes}${extra}`
  });
  renderSavedComparativos();
}

function renderSavedComparativos() {
  const container = document.getElementById('pn-saved-comps');
  if (!container) return;

  const comps = STATE.analisesSalvas.filter(a => a.type === 'comparativo').slice(0, 5);
  if (!comps.length) {
    container.innerHTML = '';
    return;
  }

  container.innerHTML = `
    <div class="pn-chart-card pn-saved-comps">
    <div class="pn-chart-title"><div class="pn-ct-bar pn-ct-bar--age"></div>COMPARATIVOS SALVOS</div>
      ${comps.map(c => `
        <div class="pn-analysis-item">
          <div class="pn-ai-icon"><span aria-hidden="true">${iconSvg('compare')}</span></div>
          <div class="pn-ai-info">
            <div class="pn-ai-title">${esc(getAnalysisDisplayLabel(c))}</div>
            <div class="pn-ai-sub">${esc(c.date || '')}</div>
          </div>
        </div>
      `).join('')}
    </div>
  `;
}

// ==========================================
// CANDIDATE SEARCH
// ==========================================
async function renderCandidateSearch(dados, query, cargoKey) {
  const agg = aggregateAll(dados, cargoKey ? { cargoKey } : {});
  const q = query.toLowerCase();
  const context = getCandidateSearchContext();
  const matches = agg.candidatos.filter(c =>
    c.nome.toLowerCase().includes(q) ||
    String(c.numero).includes(q) ||
    (c.partido || '').toLowerCase().includes(q)
  ).slice(0, 20);

  const container = document.getElementById('pn-results-cand');
  if (!container) return;
  if (matches.length === 0) {
    renderCandidateEmptyState('not-found');
    return;
  }

  const dossiers = matches.map(c => buildCandidateDossierData(dados, cargoKey, c.id));
  container.innerHTML = dossiers.map(dossier => renderCandidateDossierCard(dossier, context)).join('');
  initCandidatePhotoFallbacks(container);
  await renderCandidateHistories(container, matches, cargoKey, context);
}

function renderCandidateDossierCard(dossier, context) {
  const candidate = dossier?.candidate;
  if (!candidate) return '';
  const zoneRows = dossier.melhoresZonas.slice(0, 3);
  const sectionRows = dossier.melhoresSecoes.slice(0, 4);
  const adversarios = dossier.adversarios.slice(0, 3);
  const affiliationItems = [
    candidate.partido ? `<span><strong>Partido</strong> ${esc(candidate.partido)}</span>` : '',
    candidate.federacao ? `<span><strong>Federação</strong> ${esc(candidate.federacao)}</span>` : '',
  ].filter(Boolean);
  const affiliationHtml = affiliationItems.length
    ? `<div class="pn-dossier-affiliation">${affiliationItems.join('')}</div>`
    : '';
  const identityHtml = renderCandidateIdentity(candidate);

  return `
    <div class="pn-cand-card pn-candidate-dossier" data-candidate-id="${esc(candidate.id)}" data-numero="${escapeHTML(candidate.numero)}" data-cargo="${escapeHTML(candidate.cargo)}">
      <div class="pn-dossier-top">
        ${candidateAvatarHtml(candidate, context)}
        <div class="pn-cc-info">
          <span class="pn-dossier-eyebrow">Dossiê de candidato</span>
          <div class="pn-cc-name">${esc(candidate.nome)}</div>
          ${affiliationHtml}
          ${identityHtml}
          <div class="pn-cc-detail">
            <span class="pn-cc-tag">${esc(candidate.cargo)}</span>
            <span class="pn-cc-tag">Nº ${esc(candidate.numero)}</span>
          </div>
        </div>
        <div class="pn-dossier-score">
          <strong class="pn-cc-votes">${formatNum(candidate.votos)}</strong>
          <span>${formatPercent(candidate.percentual)} dos votos</span>
          <small>#${formatNum(candidate.rank)} no cargo</small>
        </div>
      </div>

      <div class="pn-dossier-grid">
        <div class="pn-dossier-block">
          <span>Melhores zonas</span>
          ${renderDossierMiniRows(zoneRows, row => `
            <div><strong>Zona ${esc(row.zona)}</strong><small>${formatNum(row.votos)} votos · ${formatPercent(row.percentual)}</small></div>
            <em>#${formatNum(row.rank)}</em>
          `)}
        </div>
        <div class="pn-dossier-block">
          <span>Melhores seções</span>
          ${renderDossierMiniRows(sectionRows, row => `
            <div><strong>Seção ${esc(row.secao)}</strong><small>Zona ${esc(row.zona)} · ${formatNum(row.votos)} votos</small></div>
            <em>#${formatNum(row.rank)}</em>
          `)}
        </div>
        <div class="pn-dossier-block">
          <span>Contra adversários</span>
          ${renderDossierMiniRows(adversarios, row => `
            <div><strong>${esc(row.nome)}</strong><small>${formatNum(row.votos)} votos · ${formatPercent(row.percentual)}</small></div>
            <em>${row.diferenca >= 0 ? '+' : ''}${formatNum(row.diferenca)}</em>
          `)}
        </div>
      </div>

      <div class="pn-candidate-history" data-candidate-history="${esc(candidate.id)}">
        <span>Evolução histórica</span>
        <small>Buscando eleições anteriores compatíveis...</small>
      </div>
    </div>
  `;
}

function renderCandidateIdentity(candidate) {
  const canonical = candidate.canonicalIdentity || getCandidateCanonicalIdentity(candidate, candidate);
  const canonicalOfficialId = canonical.id_oficial_tse || getCandidateOfficialId(candidate);
  const canonicalBasisText = canonicalOfficialId
    ? `TSE ${canonicalOfficialId}`
    : `${canonical.nome_urna || candidate.nome || 'Nome nao informado'} · ${candidate.cargo || getCargoMeta(candidate.cargoKey).label} · Nº ${candidate.numero || 'N/D'}`;
  const canonicalConfidence = canonical.nivel_confianca || candidate.canonicalConfidence || (canonicalOfficialId ? 'alta' : 'media');
  const canonicalCriteria = Array.isArray(canonical.criterios) && canonical.criterios.length
    ? canonical.criterios.join(', ')
    : (canonicalOfficialId ? 'identificador oficial' : 'nome normalizado, cargo e numero');

  return `
    <div class="pn-dossier-identity">
      <span><strong>Identidade canônica</strong> ${esc(canonicalBasisText)}</span>
      <span><strong>Confiança</strong> ${esc(canonicalConfidence)} · ${esc(canonicalCriteria)}</span>
    </div>
  `;
}

function renderDossierMiniRows(rows, renderer) {
  if (!rows.length) return '<div class="pn-dossier-empty">Sem votos suficientes neste recorte.</div>';
  return `
    <div class="pn-dossier-mini-list">
      ${rows.map(row => `<div class="pn-dossier-mini-row">${renderer(row)}</div>`).join('')}
    </div>
  `;
}

async function renderCandidateHistories(container, matches, cargoKey, context) {
  if (!container || !context?.municipio || !context?.ano || !cargoKey) return;
  const eleicoes = getEleicoesDisponiveisMunicipio(context.uf, context.municipio)
    .filter(ano => compareElectionValues(ano, context.ano) <= 0);
  if (eleicoes.length < 2) {
    container.querySelectorAll('.pn-candidate-history').forEach(el => {
      el.innerHTML = '<span>Evolução histórica</span><small>Sem eleição anterior compatível no catálogo público.</small>';
    });
    return;
  }

  const dataByAno = new Map();
  await Promise.all(eleicoes.map(async ano => {
    const dadosAno = await fetchDados(context.municipio, ano, { silent: true });
    if (dadosAno) dataByAno.set(ano, dadosAno);
  }));

  matches.forEach(candidate => {
    const rows = eleicoes
      .map(ano => {
        const dadosAno = dataByAno.get(ano);
        if (!dadosAno) return null;
        if (!extractCargoKeys(dadosAno).includes(cargoKey)) return null;
        const agg = aggregateAll(dadosAno, { cargoKey });
        const match = findHistoricalCandidateMatch(agg.candidatos, candidate);
        const found = match?.candidate;
        if (!found) return null;
        return {
          ano,
          votos: Number(found.votos || 0),
          percentual: agg.totalVotosValidos > 0 ? (Number(found.votos || 0) / agg.totalVotosValidos) * 100 : 0,
          matchConfidence: match.confidence,
          matchReasons: match.reasons,
        };
      })
      .filter(Boolean);

    const target = container.querySelector(`[data-candidate-history="${CSS.escape(candidate.id)}"]`);
    if (!target) return;
    target.innerHTML = renderCandidateHistoryRows(rows);
  });
}

function renderCandidateHistoryRows(rows) {
  if (rows.length < 2) {
    return '<span>Evolução histórica</span><small>Sem vínculo histórico confiável para este cargo e identidade. Critérios avaliados: identificador oficial, nome normalizado, partido, cargo e número.</small>';
  }

  const first = rows[0];
  const last = rows[rows.length - 1];
  const delta = last.votos - first.votos;
  return `
    <span>Evolução histórica</span>
    <div class="pn-history-list">
      ${rows.map(row => `
        <div class="pn-history-row">
          <strong>${esc(formatElectionLabel(row.ano))}</strong>
          <small>
            ${formatNum(row.votos)} votos · ${formatPercent(row.percentual)}
            ${renderMatchConfidence(row)}
          </small>
        </div>
      `).join('')}
    </div>
    <strong class="pn-history-delta ${delta >= 0 ? 'positive' : 'negative'}">${formatSignedNum(delta)} votos</strong>
  `;
}

function renderMatchConfidence(row) {
  const label = formatMatchConfidence(row.matchConfidence);
  if (!label) return '';
  const reasons = Array.isArray(row.matchReasons) && row.matchReasons.length
    ? ` title="Critérios: ${esc(row.matchReasons.join(', '))}"`
    : '';
  return `<span class="pn-history-confidence ${esc(row.matchConfidence)}"${reasons}>Vínculo: ${esc(label)}</span>`;
}

function formatMatchConfidence(confidence) {
  const labels = {
    alta: 'alta confiança',
    media: 'confiança média',
    baixa: 'baixa confiança'
  };
  return labels[confidence] || '';
}

function getCandidateSearchContext() {
  const recorte = STATE.recorte || getGlobalRecorte();
  if (recorte.complete) {
    return {
      ano: recorte.ano,
      uf: recorte.uf,
      municipio: recorte.municipio
    };
  }

  return {
    ano: document.getElementById('pn-sel-eleicao-cand')?.value || '',
    uf: document.getElementById('pn-sel-uf-cand')?.value || '',
    municipio: document.getElementById('pn-sel-municipio-cand')?.value || ''
  };
}

function candidateAvatarHtml(candidate, context) {
  const sources = buildCandidatePhotoSources(candidate, context);
  const initials = getInitials(candidate.nome);
  const firstSource = sources[0] || '';

  return `
    <div class="pn-cand-avatar">
      ${firstSource ? `<img src="${escapeHTML(firstSource)}" data-photo-sources="${escapeHTML(JSON.stringify(sources))}" data-photo-index="0" alt="Foto de ${esc(candidate.nome)}">` : ''}
      <span class="pn-cand-initials" aria-hidden="true" ${firstSource ? 'hidden' : ''}>${esc(initials)}</span>
    </div>
  `;
}

function buildCandidatePhotoSources(candidate, context) {
  const sources = [];
  const explicit = normalizeCandidatePhotoRef(candidate.foto, context);
  if (explicit) sources.push(explicit);

  const bases = getCandidatePhotoBaseDirs(context);
  const numero = String(candidate.numero || '').trim();
  if (!numero) return uniqueStrings(sources);

  const singularCargo = normalizeFileToken(candidate.cargo);
  const qualifiedFileNames = [
    singularCargo ? `${singularCargo}_${numero}.jpg` : '',
    candidate.cargoKey ? `${normalizeFileToken(candidate.cargoKey)}_${numero}.jpg` : '',
  ].filter(Boolean);
  const rawNumberFile = `${numero}.jpg`;
  const fileNames = shouldPreferQualifiedPhoto(candidate)
    ? [...qualifiedFileNames, rawNumberFile]
    : [rawNumberFile, ...qualifiedFileNames];

  bases.forEach(base => {
    fileNames.forEach(file => sources.push(`${base}/${encodeURIComponent(file)}`));
  });

  return uniqueStrings(sources);
}

function normalizeCandidatePhotoRef(photoRef, context) {
  if (!photoRef || /^https?:\/\//i.test(String(photoRef))) return '';

  const ref = String(photoRef).trim();
  if (!ref) return '';
  if (ref.startsWith('/')) return encodeURI(ref).replace(/#/g, '%23');
  if (ref.includes('/')) return encodeURI(`/${ref.replace(/^\/+/, '')}`).replace(/#/g, '%23');

  const base = getCandidatePhotoBaseDirs(context)[0];
  return base ? `${base}/${encodeURIComponent(ref)}` : '';
}

function shouldPreferQualifiedPhoto(candidate) {
  return [
    'presidentes',
    'governadores',
    'senadores',
    'deputados_federais',
    'deputados_estaduais',
    'deputados_distritais'
  ].includes(candidate.cargoKey);
}

function getCandidatePhotoBaseDirs(context) {
  const uf = String(context.uf || '').trim().toUpperCase();
  const ano = String(context.ano || '').trim();
  const municipio = toMunicipioPathSegment(context.municipio);
  if (!uf || !ano || !municipio) return [];

  const anos = [ano];
  const anoBase = ano.split('_')[0];
  if (anoBase && anoBase !== ano) anos.push(anoBase);

  return anos.map(a => `/Brasil/${encodeURIComponent(uf)}/${encodeURIComponent(municipio)}/${encodeURIComponent(a)}/Fotos_Candidatos`);
}

function toMunicipioPathSegment(municipio) {
  const normalized = String(municipio || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  return normalized.split(' ')
    .filter(Boolean)
    .map(word => word.length <= 2 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function normalizeFileToken(value) {
  return normalizeText(value).replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
}

function getInitials(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const initials = [parts[0], parts[parts.length - 1]]
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase())
    .join('');
  return initials || 'GV';
}

function uniqueStrings(values) {
  return Array.from(new Set(values.filter(Boolean)));
}

function initCandidatePhotoFallbacks(container) {
  container.querySelectorAll('.pn-cand-avatar img').forEach(img => {
    let sources = [];
    try {
      sources = JSON.parse(img.dataset.photoSources || '[]');
    } catch {
      sources = [];
    }

    const avatar = img.closest('.pn-cand-avatar');
    const fallback = avatar?.querySelector('.pn-cand-initials');

    img.addEventListener('load', () => {
      img.hidden = false;
      if (fallback) fallback.hidden = true;
    });

    img.addEventListener('error', () => {
      const nextIndex = Number(img.dataset.photoIndex || 0) + 1;
      if (nextIndex < sources.length) {
        img.dataset.photoIndex = String(nextIndex);
        img.src = sources[nextIndex];
        return;
      }

      img.hidden = true;
      if (fallback) fallback.hidden = false;
    });
  });
}

// ==========================================
// RENDERING HELPERS
// ==========================================
function kpiCard(value, label, sub, color) {
  const variant = getColorVariant(color);
  return `
    <div class="pn-kpi pn-kpi--${variant}" style="--pn-kpi-color:${esc(color)}">
      <div class="pn-kpi-value">${value}</div>
      <div class="pn-kpi-label">${label}</div>
      ${sub ? `<div class="pn-kpi-sub">${sub}</div>` : ''}
    </div>
  `;
}

function getColorVariant(color) {
  const variants = {
    [PANEL_COLORS.info]: 'info',
    [PANEL_COLORS.success]: 'success',
    [PANEL_COLORS.danger]: 'danger',
    [PANEL_COLORS.warning]: 'warning',
    [PANEL_COLORS.neutral]: 'neutral',
    [PANEL_COLORS.primary]: 'primary',
  };
  return variants[color] || 'neutral';
}

function formatVotesKpi(meta, cargoKey, defaultSub = 'Cargo selecionado') {
  const label = isLegislativeCargo(cargoKey)
    ? `Votos nominais de ${esc(meta.plural)}`
    : `Votos de ${esc(meta.plural)}`;
  const sub = isLegislativeCargo(cargoKey)
    ? `${defaultSub}. Não inclui voto de legenda quando a origem não traz esse campo.`
    : defaultSub;
  return { label, sub };
}

function formatVotesKpiForAggregate(meta, cargoKey, defaultSub = 'Cargo selecionado', agg = null) {
  if (!isLegislativeCargo(cargoKey)) {
    return { label: `Votos de ${esc(meta.plural)}`, sub: defaultSub };
  }

  const hasLegenda = Boolean(agg?.votosLegendaDisponiveis);
  return {
    label: hasLegenda
      ? `Votos válidos de ${esc(meta.plural)}`
      : `Votos nominais de ${esc(meta.plural)}`,
    sub: hasLegenda
      ? `${defaultSub}. Inclui ${formatNum(agg.totalVotosLegenda || 0)} voto(s) de legenda; nominais: ${formatNum(agg.totalVotosNominais || 0)}.`
      : `${defaultSub}. Não inclui voto de legenda quando a origem não traz esse campo.`,
  };
}

function isLegislativeCargo(cargoKey) {
  const cargo = normalizeCargoKey(cargoKey);
  return cargo === 'vereadores' || cargo.startsWith('deputados_');
}

function formatAbstencoesKpi(agg) {
  if (!agg.abstencoesDisponiveis) {
    const abstencoesMeta = agg?.fontesMetadados?.abstencoes;
    if (abstencoesMeta?.granularidade === 'municipio' && abstencoesMeta.usadoNoRecorte === false) {
      return {
        value: 'N/D',
        sub: 'Agregado municipal disponível; não distribuído por zona.'
      };
    }
    return { value: 'N/D', sub: 'Dado indisponível na origem' };
  }

  return {
    value: formatNum(agg.totalAbstencoes),
    sub: agg.totalEleitores > 0 ? formatPercent((agg.totalAbstencoes / agg.totalEleitores) * 100) : ''
  };
}

function formatBrancosNulosKpi(agg) {
  if (!agg.brancosNulosDisponiveis) return null;
  const totalBrancos = Number(agg.totalBrancos || 0);
  const totalNulos = Number(agg.totalNulos || 0);
  return {
    value: formatNum(Number(agg.totalBrancosNulos || 0)),
    sub: `Brancos: ${formatNum(totalBrancos)} · Nulos: ${formatNum(totalNulos)}`
  };
}

function isZonaAbstencoesMunicipalOnly(agg, scope) {
  const abstencoesMeta = agg?.fontesMetadados?.abstencoes;
  return scope === 'zona'
    && abstencoesMeta?.granularidade === 'municipio'
    && abstencoesMeta.usadoNoRecorte === false;
}

function renderEleitoresNote(kpisEl, agg, scope) {
  if (!kpisEl) return;

  const existing = kpisEl.parentElement?.querySelector(`[data-kpi-note="${kpisEl.id}"]`);
  if (existing) existing.remove();

  const hasEleitoresDivergence = Boolean(
    agg.eleitoresDivergencia
    && agg.eleitoresDivergencia.valor > 0
    && agg.eleitoresDivergencia.percentual >= 1
  );
  const hasZonaAbstencoesMunicipalOnly = isZonaAbstencoesMunicipalOnly(agg, scope);

  if (!hasEleitoresDivergence && !hasZonaAbstencoesMunicipalOnly) return;

  const noteParts = [];
  const metadata = agg.eleitoresMetadados || {};

  if (hasEleitoresDivergence) {
    const html = scope === 'zona'
      ? `Nota técnica: eleitores da zona somam aptos das seções da zona (${formatNum(agg.totalEleitores)}). Total municipal disponível na origem: ${formatNum(agg.municipioTotalEleitores)}. A diferença pode ocorrer por recorte territorial ou origem distinta dos aptos.`
      : `Nota técnica: eleitores do município usam o total municipal disponível na origem (${formatNum(agg.municipioTotalEleitores)}); a soma das seções carregadas é ${formatNum(agg.totalEleitoresSecoes)}. A diferença pode ocorrer por recorte territorial ou origem distinta dos aptos.`;
    const metadataText = metadata.regraCalculo
      ? ` Fonte: ${metadata.fontePrincipal}; conferencia: ${metadata.fonteSecoes}; severidade: ${metadata.severidade}.`
      : '';
    noteParts.push(html + metadataText);
  }

  const abstencoesMeta = agg?.fontesMetadados?.abstencoes;
  if (hasZonaAbstencoesMunicipalOnly) {
    noteParts.push('Abstenções por zona: N/D; a origem oficial disponível traz abstenções apenas no agregado municipal e o painel não distribui esse total entre zonas.');
  }

  kpisEl.insertAdjacentHTML('afterend', `
    <div class="pn-data-note" data-kpi-note="${escapeHTML(kpisEl.id)}" data-source="${esc(metadata.fontePrincipal || abstencoesMeta?.fonte || agg.eleitoresFonte || '')}" data-rule="${esc(metadata.regraCalculo || abstencoesMeta?.regra || '')}" data-severity="${esc(metadata.severidade || agg.eleitoresDivergencia?.severidade || 'informativo')}">
      ${esc(noteParts.join(' '))}
    </div>
  `);
}

function renderExecutiveSummary(container, agg, context = {}) {
  if (!container) return;

  const insights = buildExecutiveInsights(agg, context);
  if (!insights.length) {
    container.hidden = true;
    container.innerHTML = '';
    return;
  }

  const summaryId = `pn-summary-grid-${context.scope || container.dataset.executiveSummary || 'recorte'}`;
  container.hidden = false;
  container.innerHTML = `
    <div class="pn-summary-head">
      <h3>Resumo do recorte</h3>
      <button type="button" class="pn-summary-toggle" data-summary-toggle aria-expanded="true" aria-controls="${esc(summaryId)}">
        <span data-summary-toggle-label>Ocultar detalhes</span>
        <span class="pn-summary-toggle-count">${Math.max(0, insights.length - 1)} detalhes</span>
      </button>
    </div>
    <div class="pn-summary-grid" id="${esc(summaryId)}">
      ${insights.map((item, index) => `
        <div class="pn-summary-item ${index === 0 ? 'is-primary' : 'is-secondary'}">
          <span class="pn-summary-label">${esc(item.label)}</span>
          <span class="pn-summary-value">${esc(item.value)}</span>
        </div>
      `).join('')}
    </div>
  `;
  setExecutiveSummaryExpanded(container, !window.matchMedia('(max-width: 768px)').matches);
}

function buildExecutiveInsights(agg, context = {}) {
  const candidatos = Array.isArray(agg?.candidatos) ? agg.candidatos : [];
  const leader = candidatos[0];
  const second = candidatos[1];
  const totalVotos = Number(agg?.totalVotosValidos || 0);
  const leaderPercent = leader && totalVotos > 0 ? (leader.votos / totalVotos) * 100 : 0;
  const delta = leader && second ? leader.votos - second.votos : null;
  const strength = buildPoliticalStrengthData(agg, { partidosCatalog: STATE.partidosCatalog });
  const dominantParty = strength.partidos[0];
  const insights = [];

  if (leader) {
    insights.push({
      label: 'Líder',
      value: `${leader.nome} lidera ${context.cargoLabel || 'o cargo'} com ${formatNum(leader.votos)} votos (${formatPercent(leaderPercent)}).`
    });
  }

  if (leader && second && delta !== null) {
    const deltaPercent = totalVotos > 0 ? (delta / totalVotos) * 100 : 0;
    const disputaLabel = deltaPercent <= 7 || delta <= 100 ? 'Disputa apertada' : 'Vantagem consolidada';
    insights.push({
      label: 'Diferença',
      value: `${disputaLabel}: diferença de ${formatNum(delta)} votos entre os dois primeiros. ${leader.nome} está à frente de ${second.nome}.`
    });
  } else if (leader) {
    insights.push({
      label: 'Diferença',
      value: 'Não há segundo colocado com votos neste recorte.'
    });
  }

  if (dominantParty) {
    insights.push({
      label: 'Força partidária',
      value: `${dominantParty.nome} lidera por ${formatPercent(dominantParty.percentual)} dos votos do cargo selecionado, com ${formatNum(dominantParty.votos)} votos.`
    });
  }

  insights.push({
    label: 'Consistência dos dados',
    value: buildDataConsistencyInsight(agg, context)
  });

  return insights.slice(0, 4);
}

function buildDataConsistencyInsight(agg, context = {}) {
  const divergence = agg?.eleitoresDivergencia;
  if (divergence && divergence.valor > 0 && divergence.percentual >= 1) {
    const scopeText = context.scope === 'zona' ? 'zona' : 'município';
    return `Há divergência entre aptos municipais e soma das seções carregadas: diferença de ${formatNum(divergence.valor)} eleitor(es) no recorte de ${scopeText}; use como nota de consistência.`;
  }

  if (!agg?.abstencoesDisponiveis) {
    if (isZonaAbstencoesMunicipalOnly(agg, context.scope)) {
      return 'A consistência não tem alerta crítico; abstenções existem no agregado municipal oficial, mas não foram distribuídas por zona.';
    }
    return 'A consistência não tem alerta crítico; abstenções não disponíveis na origem.';
  }

  return 'A consistência não tem alerta crítico nos campos agregados disponíveis.';
}

function renderCompetitiveness(container, agg, context = {}) {
  if (!container) return;
  if (!agg) {
    container.replaceChildren();
    return;
  }

  const index = agg.competitividade || buildCompetitivenessIndex(agg, {
    scope: context.scope || 'municipio',
    partidosCatalog: STATE.partidosCatalog,
  });
  if (!index || index.level === 'indisponivel') {
    container.innerHTML = `
      <div class="pn-competitiveness-card is-unavailable">
        <div class="pn-competitiveness-header">
          <div>
            <div class="pn-chart-title"><div class="pn-ct-bar pn-ct-bar--info"></div>ÃNDICE DE COMPETITIVIDADE</div>
            <p>NÃ£o hÃ¡ votos suficientes entre pelo menos dois candidatos para calcular o score deste recorte.</p>
          </div>
        </div>
      </div>
    `;
    return;
  }

  const sections = Array.isArray(context.sections)
    ? context.sections
    : (Array.isArray(index.secoesDecisivas) ? index.secoesDecisivas : []);
  const availableFactors = Array.isArray(index.factors) ? index.factors.filter(factor => factor.available !== false) : [];
  const unavailableFactors = Array.isArray(index.unavailableFactors) ? index.unavailableFactors : [];
  const leaderName = index.leader?.nome || 'LÃ­der';
  const runnerName = index.runnerUp?.nome || 'segundo colocado';
  const score = Math.round(Number(index.score || 0));
  const level = index.level || 'media';
  const confidenceNote = index.confidence === 'media'
    ? '<span class="pn-competitiveness-note">ConfianÃ§a mÃ©dia: ranking parcial; use como leitura preliminar.</span>'
    : '';

  container.innerHTML = `
    <div class="pn-competitiveness-card" data-competitiveness="${esc(context.scope || index.scope || 'municipio')}" data-level="${esc(level)}">
      <div class="pn-competitiveness-header">
        <div>
          <div class="pn-chart-title"><div class="pn-ct-bar pn-ct-bar--info"></div>ÃNDICE DE COMPETITIVIDADE ELEITORAL</div>
          <p>Score calculado por margem entre lÃ­deres, concentraÃ§Ã£o partidÃ¡ria e fatores histÃ³ricos/temporais quando disponÃ­veis.</p>
        </div>
        <div class="pn-competitiveness-score pn-competitiveness-score--${esc(level)}" aria-label="Competitividade ${esc(index.label)}, ${score} de 100">
          <strong>${formatNum(score)}</strong><span>/100</span>
          <small>${esc(index.label)}</small>
        </div>
      </div>
      <div class="pn-competitiveness-main">
        <div>
          <strong>${esc(index.label)} competitividade no recorte</strong>
          <span>${esc(leaderName)} lidera ${esc(runnerName)} por ${formatNum(index.marginVotes)} voto(s), margem de ${formatPercentCompact(index.marginPct)}.</span>
        </div>
        ${index.dominantParty ? `
          <div>
            <strong>ConcentraÃ§Ã£o partidÃ¡ria</strong>
            <span>${esc(index.dominantParty.nome)} concentra ${formatPercentCompact(index.concentrationPct)} dos votos do cargo.</span>
          </div>
        ` : ''}
      </div>
      <div class="pn-competitiveness-factors" aria-label="Fatores do Ã­ndice de competitividade">
        ${availableFactors.map(factor => `
          <span class="pn-competitiveness-factor">
            <strong>${esc(factor.label)}</strong>
            <em>${formatNum(Math.round(Number(factor.score || 0)))}/100</em>
          </span>
        `).join('')}
        ${unavailableFactors.slice(0, 2).map(factor => `
          <span class="pn-competitiveness-factor is-muted" title="${esc(factor.detail || '')}">
            <strong>${esc(factor.label)}</strong>
            <em>N/D</em>
          </span>
        `).join('')}
      </div>
      ${renderCompetitivenessSections(sections)}
      ${confidenceNote}
    </div>
  `;
}

function renderCompetitivenessSections(sections = []) {
  const visible = Array.isArray(sections) ? sections.slice(0, 5) : [];
  if (!visible.length) return '';

  return `
    <div class="pn-competitiveness-sections">
      <div class="pn-competitiveness-subtitle">SeÃ§Ãµes decisivas</div>
      <div class="pn-competitiveness-section-list">
        ${visible.map(row => `
          <div class="pn-competitiveness-section-row">
            <div>
              <strong>Zona ${esc(row.zona || '-')} Â· SeÃ§Ã£o ${esc(row.secao || '-')}</strong>
              <span>Margem de ${formatNum(row.marginVotes)} voto(s) em ${formatNum(row.totalVotes)} votos.</span>
            </div>
            <span class="pn-competitiveness-mini-score">${formatNum(Math.round(Number(row.score || 0)))}</span>
          </div>
        `).join('')}
      </div>
    </div>
  `;
}

function renderRanking(container, candidatos, options = {}) {
  if (!container) return;
  if (!candidatos.length) {
    container.innerHTML = '<div class="pn-empty"><p>Nenhum candidato com votos para o cargo selecionado.</p></div>';
    return;
  }
  const serverMeta = candidatos.rankingMeta;
  const rankingView = serverMeta
    ? {
      visible: candidatos,
      total: serverMeta.total,
      hiddenCount: Math.max(0, Number(serverMeta.total || 0) - candidatos.length),
      truncated: Boolean(serverMeta.hasMore || Number(serverMeta.total || 0) > candidatos.length),
      hasMore: Boolean(serverMeta.hasMore),
      limit: serverMeta.pageSize || candidatos.length,
      page: serverMeta.page || 1,
    }
    : buildRankingViewModel(candidatos, { limit: 15 });
  const top = rankingView.visible;
  const maxVotos = top[0]?.votos || 1;
  const leaderVotes = top[0]?.votos || 0;
  container.innerHTML = `
    <table class="pn-ranking">
      <thead><tr><th>#</th><th>Candidato</th><th>Cargo</th><th style="text-align:right">Dif.</th><th style="text-align:right">Votos</th></tr></thead>
      <tbody>
        ${top.map((c, i) => {
          const percentOfLeader = maxVotos > 0 ? Math.max(4, (Number(c.votos || 0) / maxVotos) * 100) : 0;
          const deltaToLeader = i === 0 ? 0 : leaderVotes - Number(c.votos || 0);
          const title = i === 0
            ? `Líder com ${formatNum(c.votos)} votos`
            : `Diferença para o líder: ${formatNum(deltaToLeader)} voto(s)`;
          return `
          <tr class="${i === 0 ? 'is-leader' : ''}" title="${esc(title)}" aria-label="${esc(title)}">
            <td class="pn-rank-pos">${i + 1}º</td>
            <td class="pn-rank-name">
              <span class="pn-rank-main">
                <span>${esc(c.nome)}</span>
                <span class="pn-rank-number">· ${esc(c.numero)}</span>
                ${i === 0 ? '<span class="pn-rank-badge">Líder</span>' : ''}
              </span>
            </td>
            <td style="font-size:11px;color:var(--pn-text-secondary)">${esc(c.cargo)}</td>
            <td class="pn-rank-delta">${i === 0 ? 'Dif. 0' : `Dif. ${formatNum(deltaToLeader)}`}</td>
            <td class="pn-rank-votes">
              ${formatNum(c.votos)}
              <div class="pn-rank-bar-track" aria-hidden="true">
                <span class="pn-rank-bar" style="width:${percentOfLeader.toFixed(2)}%"></span>
              </div>
            </td>
          </tr>
        `;
        }).join('')}
      </tbody>
    </table>
    ${rankingView.truncated ? `<div class="pn-list-note">Mostrando ${formatNum(top.length)} de ${formatNum(rankingView.total)} candidatos. Use filtros ou busca para reduzir o recorte em bases grandes.</div>` : ''}
    ${rankingView.hasMore && options.scope ? `
      <div class="pn-ranking-actions">
        <button type="button" class="pn-btn pn-btn-outline pn-ranking-more" data-ranking-load-more="${esc(options.scope)}">
          Carregar mais candidatos
        </button>
      </div>
    ` : ''}
  `;
}

// ==========================================
// CHARTS — Chart.js
// ==========================================
function renderPoliticalStrength(container, agg) {
  if (!container) return;

  if (!agg) {
    container.innerHTML = '';
    return;
  }

  const data = agg.forcaPolitica
    ? {
      totalVotos: Number(agg.totalVotosValidos || 0),
      partidos: agg.forcaPolitica.partidos || [],
      federacoes: agg.forcaPolitica.federacoes || [],
    }
    : buildPoliticalStrengthData(agg, { partidosCatalog: STATE.partidosCatalog });
  container.innerHTML = `
    <div class="pn-chart-title"><div class="pn-ct-bar pn-ct-bar--strength"></div>FORÇA POLÍTICA</div>
    <div class="pn-strength-grid">
      ${renderPoliticalStrengthBlock('Força por Partido', data.partidos, 'Partido')}
      ${renderPoliticalStrengthBlock('Força por Federação/Coligação', data.federacoes, 'Federação/Coligação')}
    </div>
    <div class="pn-strength-note">Percentuais calculados sobre ${formatNum(data.totalVotos)} votos do cargo selecionado.</div>
  `;
}

function renderPoliticalStrengthBlock(title, rows, groupLabel) {
  const visibleRows = rows.slice(0, 10);

  if (!visibleRows.length) {
    return `
      <div class="pn-strength-block">
        <div class="pn-strength-subtitle">${esc(title)}</div>
        <div class="pn-empty pn-strength-empty"><p>Nenhum voto encontrado para o cargo selecionado.</p></div>
      </div>
    `;
  }

  return `
    <div class="pn-strength-block">
      <div class="pn-strength-subtitle">${esc(title)}</div>
      <div class="pn-table-scroll">
        <table class="pn-ranking pn-strength-table">
          <thead>
            <tr>
              <th>#</th>
              <th>${esc(groupLabel)}</th>
              <th style="text-align:right">Candidatos</th>
              <th style="text-align:right">Votos</th>
              <th style="text-align:right">%</th>
            </tr>
          </thead>
          <tbody>
            ${visibleRows.map((row, index) => `
              <tr>
                <td class="pn-rank-pos">${index + 1}º</td>
                <td>
                  <div class="pn-strength-group">
                    <span class="pn-strength-name">${esc(row.nome)}</span>
                  </div>
                </td>
                <td class="pn-rank-votes">${formatNum(row.candidatos)}</td>
                <td class="pn-rank-votes">${formatNum(row.votos)}</td>
                <td class="pn-rank-votes">${formatPercent(row.percentual)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    </div>
  `;
}

function renderGeneroChart(genero) {
  destroyChart('genero');
  clearProfileChartSkeleton('genero');
  const rows = [
    ['Feminino', genero['Feminino']],
    ['Masculino', genero['Masculino']],
    ['Não Informado', genero['Não Informado']],
  ];
  renderChartSummary('genero', 'Resumo textual do gráfico de gênero', rows);
  const ctx = document.getElementById('pn-chart-genero')?.getContext('2d');
  if (!ctx) return;
  STATE.charts.genero = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: ['Feminino', 'Masculino', 'Não Informado'],
      datasets: [{
        data: [genero['Feminino'], genero['Masculino'], genero['Não Informado']],
        backgroundColor: [PANEL_COLORS.info, PANEL_COLORS.success, '#94a3b8'],
        borderWidth: 0
      }]
    },
    options: {
      cutout: '64%',
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            color: '#5c4d44',
            font: { family: 'Inter', size: 10, weight: '700' },
            boxWidth: 12,
            padding: 10
          }
        }
      }
    }
  });
}

function renderIdadeChart(idade) {
  destroyChart('idade');
  clearProfileChartSkeleton('idade');
  const rows = [
    ['16 a 24 anos', idade['16 a 24 anos']],
    ['25 a 49 anos', idade['25 a 49 anos']],
    ['50 a 69 anos', idade['50 a 69 anos']],
    ['70 anos ou mais', idade['70 anos ou mais']],
    ['Não Informado', idade['Não Informado']],
  ];
  renderChartSummary('idade', 'Resumo textual do gráfico de faixa etária', rows);
  const ctx = document.getElementById('pn-chart-idade')?.getContext('2d');
  if (!ctx) return;
  STATE.charts.idade = new Chart(ctx, {
    type: 'bar',
    data: {
      labels: ['16-24', '25-49', '50-69', '70+', 'N/I'],
      datasets: [{
        label: 'Eleitores',
        data: [idade['16 a 24 anos'], idade['25 a 49 anos'], idade['50 a 69 anos'], idade['70 anos ou mais'], idade['Não Informado']],
        backgroundColor: PANEL_COLORS.info,
        borderRadius: 8,
        barThickness: 16,
        maxBarThickness: 18,
        categoryPercentage: 0.72
      }]
    },
    options: {
      maintainAspectRatio: false,
      scales: {
        y: { display: false },
        x: {
          grid: { display: false },
          ticks: {
            color: '#5c4d44',
            font: { family: 'Inter', size: 10, weight: '700' }
          }
        }
      },
      plugins: { legend: { display: false } }
    }
  });
}

function renderInstrucaoChart(instrucao) {
  destroyChart('instrucao');
  clearProfileChartSkeleton('instrucao');
  const labels = ['Analfabeto', 'Lê e Escreve', 'Fund. Incompleto', 'Fund. Completo', 'Médio Incompleto', 'Médio Completo', 'Sup. Incompleto', 'Sup. Completo', 'Não Informado'];
  const data = labels.map(l => instrucao[l] || 0);
  renderChartSummary('instrucao', 'Resumo textual do gráfico de grau de instrução', labels.map((label, index) => [label, data[index]]));
  const ctx = document.getElementById('pn-chart-instrucao')?.getContext('2d');
  if (!ctx) return;
  STATE.charts.instrucao = new Chart(ctx, {
    type: 'bar',
    data: {
      labels,
      datasets: [{
        label: 'Eleitores',
        data,
        backgroundColor: PANEL_COLORS.neutral,
        borderRadius: 8,
        barThickness: 10,
        maxBarThickness: 12,
        categoryPercentage: 0.74
      }]
    },
    options: {
      indexAxis: 'y',
      maintainAspectRatio: false,
      layout: { padding: { right: 24 } },
      scales: {
        y: {
          grid: { display: false },
          ticks: {
            color: '#5c4d44',
            font: { family: 'Inter', size: 10, weight: '700' }
          }
        },
        x: { display: false }
      },
      plugins: { legend: { display: false } }
    }
  });
  syncProfileChartDisclosureForViewport({ reset: true });
}

function buildProfileChartExportData(agg) {
  const genero = agg?.genero || {};
  const idade = agg?.idade || {};
  const instrucao = agg?.instrucao || {};
  return {
    genero: [
      { label: 'Feminino', value: Number(genero['Feminino'] || 0) },
      { label: 'Masculino', value: Number(genero['Masculino'] || 0) },
      { label: 'Não Informado', value: Number(genero['Não Informado'] || 0) },
    ],
    idade: [
      { label: '16 a 24 anos', value: Number(idade['16 a 24 anos'] || 0) },
      { label: '25 a 49 anos', value: Number(idade['25 a 49 anos'] || 0) },
      { label: '50 a 69 anos', value: Number(idade['50 a 69 anos'] || 0) },
      { label: '70 anos ou mais', value: Number(idade['70 anos ou mais'] || 0) },
      { label: 'Não Informado', value: Number(idade['Não Informado'] || 0) },
    ],
    instrucao: [
      'Analfabeto',
      'Lê e Escreve',
      'Fund. Incompleto',
      'Fund. Completo',
      'Médio Incompleto',
      'Médio Completo',
      'Sup. Incompleto',
      'Sup. Completo',
      'Não Informado'
    ].map(label => ({ label, value: Number(instrucao[label] || 0) }))
  };
}

function renderChartSummary(name, caption, rows) {
  const canvas = document.getElementById(`pn-chart-${name}`);
  const summary = document.getElementById(`pn-summary-${name}`);
  if (!summary) return;

  syncCanvasAccessibility(canvas, summary, caption);
  summary.innerHTML = buildChartSummaryHtml(caption, rows, { formatValue: formatNum });
}

function destroyChart(name) {
  if (STATE.charts[name]) {
    STATE.charts[name].destroy();
    STATE.charts[name] = null;
  }
}

// ==========================================
// SAVED ANALYSES — localStorage
// ==========================================
function loadSavedAnalyses() {
  try { STATE.analisesSalvas = JSON.parse(localStorage.getItem(SAVED_ANALYSES_STORAGE_KEY) || '[]'); } catch { STATE.analisesSalvas = []; }
  if (!Array.isArray(STATE.analisesSalvas)) STATE.analisesSalvas = [];
  renderSavedAnalyses();
  renderSavedComparativos();
}

function saveAnalysis(type, data) {
  const dedupeKey = buildAnalysisDedupeKey(type, data);
  const item = normalizeSavedAnalysisRecord(type, { ...data, dedupeKey });

  const exists = STATE.analisesSalvas.find(a =>
    (dedupeKey && a.dedupeKey === dedupeKey) || (!a.dedupeKey && a.label === item.label)
  );
  if (exists) {
    Object.assign(exists, normalizeSavedAnalysisRecord(type, {
      ...data,
      id: exists.id,
      title: exists.title || data.title || data.label,
      dedupeKey,
    }, { existing: exists, idFactory: () => exists.id }));
  } else {
    STATE.analisesSalvas.unshift(item);
    if (STATE.analisesSalvas.length > 50) STATE.analisesSalvas.pop();
  }

  persistSavedAnalyses();
}

function renderSavedAnalyses() {
  const container = document.getElementById('pn-lista-analises');
  const countEl = document.getElementById('pn-saved-count');
  if (!container) return;

  const query = normalizeText(document.getElementById('pn-search-analises')?.value || '');
  const filtered = query
    ? STATE.analisesSalvas.filter(a => normalizeText(`${getAnalysisDisplayLabel(a)} ${a.type || ''} ${a.date || ''}`).includes(query))
    : STATE.analisesSalvas;

  if (filtered.length === 0) {
    const title = query ? 'Nenhuma análise encontrada.' : 'Nenhuma análise salva ainda.';
    const text = query
      ? 'Ajuste a busca ou volte para o painel Município para criar uma nova análise.'
      : 'Execute uma análise e clique em salvar para montar seu histórico.';
    container.innerHTML = emptyStateHtml({
      scope: 'analises',
      icon: 'bookmark',
      title,
      text,
      extra: '<div class="pn-empty-actions"><button type="button" class="pn-empty-action" data-saved-empty-action="municipio">Começar análise</button></div>'
    });
    if (countEl) countEl.textContent = query ? '' : 'Hist\u00f3rico local vazio';
    return;
  }

  const icons = { municipio: 'building', zona: 'pin', candidato: 'user', comparativo: 'compare' };

  container.innerHTML = filtered.map(a => {
    const label = getAnalysisDisplayLabel(a);
    const editing = String(STATE.editingAnalysisId) === String(a.id);
    return `
    <div class="pn-analysis-item ${editing ? 'is-editing' : ''}" data-id="${esc(a.id)}" role="button" tabindex="0" aria-label="Abrir analise ${esc(label)}">
      <div class="pn-ai-icon"><span aria-hidden="true">${iconSvg(icons[a.type] || 'chart')}</span></div>
      <div class="pn-ai-info">
        ${editing ? `
          <div class="pn-ai-rename-form">
            <input type="text" class="pn-ai-rename-input" value="${esc(label)}" aria-label="Novo nome da analise">
            <div class="pn-ai-rename-actions">
              <button type="button" data-analysis-action="rename-save" data-id="${esc(a.id)}" aria-label="Salvar nome">Salvar</button>
              <button type="button" data-analysis-action="rename-cancel" data-id="${esc(a.id)}" aria-label="Cancelar renomeacao">Cancelar</button>
            </div>
          </div>
        ` : `
          <div class="pn-ai-title">${esc(label)}</div>
          <div class="pn-ai-sub">${esc(getSavedAnalysisSubLabel(a))}</div>
        `}
      </div>
      <div class="pn-ai-actions">
        <button type="button" title="Abrir analise" aria-label="Abrir analise" data-analysis-action="open" data-id="${esc(a.id)}">Abrir</button>
        <button type="button" title="Renomear" aria-label="Renomear analise" data-analysis-action="rename" data-id="${esc(a.id)}">Renomear</button>
        <button type="button" title="Duplicar" aria-label="Duplicar analise" data-analysis-action="duplicate" data-id="${esc(a.id)}">Duplicar</button>
        <button type="button" title="Exportar" aria-label="Exportar analise" data-analysis-action="export" data-id="${esc(a.id)}">Exportar</button>
        <button type="button" title="Excluir" aria-label="Excluir analise" data-analysis-action="delete" data-id="${esc(a.id)}">Excluir</button>
      </div>
    </div>
  `;
  }).join('');

  if (countEl) countEl.textContent = `${STATE.analisesSalvas.length} an\u00e1lise(s) salva(s) \u00b7 Salvas neste navegador`;
}

// Search analyses
document.getElementById('pn-search-analises')?.addEventListener('input', () => renderSavedAnalyses());

function handleSavedEmptyAction(e) {
  const button = e.target.closest('[data-saved-empty-action]');
  if (!button) return;
  e.preventDefault();
  selectPanelSection('municipio');
  setMunicipioAnalysisCompact(false);
  scrollMunicipioFiltersIntoView();
  window.setTimeout(() => focusPanelField('pn-sel-uf-mun'), 120);
}

function persistSavedAnalyses() {
  localStorage.setItem(SAVED_ANALYSES_STORAGE_KEY, JSON.stringify(STATE.analisesSalvas));
  renderSavedAnalyses();
  renderSavedComparativos();
}

function getSavedAnalysis(id) {
  return STATE.analisesSalvas.find(a => String(a.id) === String(id)) || null;
}

function handleSavedAnalysisClick(e) {
  const actionButton = e.target.closest('[data-analysis-action]');
  const item = e.target.closest('.pn-analysis-item');
  if (!item) return;

  const id = actionButton?.dataset.id || item.dataset.id;
  if (!id) return;

  if (actionButton) {
    e.preventDefault();
    e.stopPropagation();
    handleSavedAnalysisAction(actionButton.dataset.analysisAction, id, item);
    return;
  }

  if (e.target.closest('.pn-ai-rename-form')) return;
  openSavedAnalysis(id);
}

function handleSavedAnalysisKeydown(e) {
  if (e.key !== 'Enter' && e.key !== ' ') return;
  if (e.target.closest('[data-analysis-action], .pn-ai-rename-form')) return;
  const item = e.target.closest('.pn-analysis-item');
  if (!item) return;
  e.preventDefault();
  openSavedAnalysis(item.dataset.id);
}

function handleSavedAnalysisAction(action, id, item) {
  switch (action) {
    case 'open':
      openSavedAnalysis(id);
      break;
    case 'rename':
      startRenameSavedAnalysis(id);
      break;
    case 'rename-save':
      saveRenameSavedAnalysis(id, item);
      break;
    case 'rename-cancel':
      STATE.editingAnalysisId = null;
      renderSavedAnalyses();
      break;
    case 'duplicate':
      duplicateSavedAnalysis(id);
      break;
    case 'export':
      exportSavedAnalysis(id);
      break;
    case 'delete':
      deleteSavedAnalysis(id);
      break;
  }
}

async function openSavedAnalysis(id) {
  const analysis = getSavedAnalysis(id);
  if (!analysis) return;

  try {
    if (analysis.type === 'municipio') {
      await restoreMunicipioAnalysis(analysis);
    } else if (analysis.type === 'zona') {
      await restoreZonaAnalysis(analysis);
    } else if (analysis.type === 'comparativo') {
      await restoreComparativoAnalysis(analysis);
    } else {
      showToast('Tipo de analise salva ainda nao pode ser reaberto.', 'warning');
      return;
    }
    showToast('Analise aberta.', 'info');
  } catch (err) {
    console.error('[Painel] Erro ao abrir analise salva:', err);
    showToast('Nao foi possivel abrir a analise salva.', 'error');
  }
}

function applyAnalysisToGlobalContext(analysis, values = {}) {
  const municipio = values.municipio ?? analysis.municipio ?? '';
  const ano = values.ano ?? analysis.ano ?? '';
  const uf = values.uf ?? analysis.uf ?? inferUfForMunicipio(municipio);
  const cargoKey = analysis.cargoKey || '';
  const cargoLabel = analysis.cargo || (cargoKey ? getCargoMeta(cargoKey).label : '');

  setSelectValue('pn-sel-uf-mun', uf, uf ? `${uf} — ${UF_NAMES[uf] || uf}` : '', false);
  setMunicipioField('mun', municipio, { emitInput: false });
  atualizarEleicoesMunicipio();
  setSelectValue('pn-sel-eleicao-mun', ano, ano ? formatElectionLabel(ano) : '', false);
  if (cargoKey) setSelectValue('pn-sel-cargo-mun', cargoKey, cargoLabel, false);
  closeMunicipioTypeaheads();
  updateGlobalContext({ syncSecondary: true, persist: true });
}

async function restoreMunicipioAnalysis(analysis) {
  selectPanelSection('municipio');
  const municipio = analysis.municipio || '';
  const ano = analysis.ano || '';
  const uf = analysis.uf || inferUfForMunicipio(municipio);
  setSelectValue('pn-sel-uf-mun', uf, uf ? `${uf} — ${UF_NAMES[uf] || uf}` : '', false);
  setMunicipioField('mun', municipio, { emitInput: false });
  atualizarEleicoesMunicipio();
  setSelectValue('pn-sel-eleicao-mun', ano, ano ? formatElectionLabel(ano) : '', false);
  await ensureCargoOptionsForScope('mun', analysis.cargoKey);
  setSelectValue('pn-sel-cargo-mun', analysis.cargoKey || getSelectedCargoKey('mun'), analysis.cargo || '', false);
  closeMunicipioTypeaheads();
  updateGlobalContext({ syncSecondary: true, persist: true });
  clearScopeFeedback('mun');
  await analisarMunicipio(municipio, ano, { save: false, cargoKey: analysis.cargoKey });
}

async function restoreZonaAnalysis(analysis) {
  const municipio = analysis.municipio || '';
  const ano = analysis.ano || '';
  const uf = analysis.uf || inferUfForMunicipio(municipio);
  applyAnalysisToGlobalContext(analysis, { municipio, ano, uf });
  selectPanelSection('zona', { refreshZona: false });
  setSelectValue('pn-sel-uf-zona', uf, uf ? `${uf} — ${UF_NAMES[uf] || uf}` : '', false);
  setMunicipioField('zona', municipio, { emitInput: false });
  setSelectValue('pn-sel-eleicao-zona', ano, ano ? formatElectionLabel(ano) : '', false);
  await ensureCargoOptionsForScope('zona', analysis.cargoKey);
  setSelectValue('pn-sel-cargo-zona', analysis.cargoKey || getSelectedCargoKey('zona'), analysis.cargo || '', false);
  await atualizarZonasPainel();
  setSelectValue('pn-sel-zona', analysis.zona || '', analysis.zona ? `Zona ${analysis.zona}` : '', false);
  updateZoneReadyState();
  clearScopeFeedback('zona');
  await analisarZona(municipio, ano, analysis.zona, { save: false, cargoKey: analysis.cargoKey });
}

async function restoreComparativoAnalysis(analysis) {
  const municipio = analysis.municipio || '';
  const ano = analysis.ano || '';
  const uf = analysis.uf || inferUfForMunicipio(municipio);
  applyAnalysisToGlobalContext(analysis, { municipio, ano, uf });
  selectPanelSection('comparativo');
  setSelectValue('pn-sel-uf-comp', uf, uf ? `${uf} — ${UF_NAMES[uf] || uf}` : '', false);
  setMunicipioField('comp', municipio, { emitInput: false });
  setSelectValue('pn-sel-eleicao-comp', ano, ano ? formatElectionLabel(ano) : '', false);
  await ensureCargoOptionsForScope('comp', analysis.cargoKey);
  setSelectValue('pn-sel-cargo-comp', analysis.cargoKey || getSelectedCargoKey('comp'), analysis.cargo || '', false);
  clearScopeFeedback('comp');

  const dados = await fetchDados(municipio, ano);
  if (!dados) return;
  const cargos = extractCargoKeys(dados);
  const cargoKey = chooseDefaultCargoKey(cargos, analysis.cargoKey);
  STATE.comparativo = {
    dados,
    municipio,
    ano,
    cargos,
    cargoKey,
    selectedIds: (analysis.candidatos || []).map(c => c.id || `${cargoKey}_${c.numero}`).filter(Boolean),
    query: ''
  };
  renderComparativo();
}

function selectPanelSection(section, options = {}) {
  updateSidebarCurrentState(section);
  document.querySelectorAll('.pn-section').forEach(s => s.classList.remove('active'));
  document.getElementById(`sec-${section}`)?.classList.add('active');

  const recorte = STATE.recorte || getGlobalRecorte();
  const scopeBySection = {
    zona: 'zona',
    candidato: 'cand',
    comparativo: 'comp'
  };
  const scope = scopeBySection[section];
  if (scope && recorte.complete) {
    syncScopeWithGlobal(scope, recorte);
    if (scope === 'zona' && options.refreshZona !== false) void atualizarZonasPainel();
  }
  updateSectionHints(recorte);

  if (section === 'municipio' && STATE.municipioAnalise) {
    const guide = document.getElementById('pn-start-guide');
    if (guide) guide.hidden = true;
  }
}

function updateSidebarCurrentState(section) {
  document.querySelectorAll('.pn-sidebar-item, .pn-mobile-secondary-action').forEach(item => {
    const isActive = item.getAttribute('data-section') === section;
    item.classList.toggle('active', isActive);
    if (isActive) item.setAttribute('aria-current', 'page');
    else item.removeAttribute('aria-current');
  });
}

function setSelectValue(id, value, label = value, dispatch = true) {
  const select = document.getElementById(id);
  if (!select) return;
  select.disabled = false;
  const stringValue = String(value || '');
  if (stringValue && !Array.from(select.options).some(opt => opt.value === stringValue)) {
    const opt = document.createElement('option');
    opt.value = stringValue;
    opt.textContent = label || stringValue;
    select.appendChild(opt);
  }
  select.value = stringValue;
  syncCustomSelect(select);
  if (dispatch) select.dispatchEvent(new Event('change', { bubbles: true }));
}

function syncCustomSelect(select) {
  if (!select) return;
  const wrapper = select.closest('.pn-custom-select-container') || select.closest('.pn-select-wrapper');
  const trigger = wrapper?.querySelector('.pn-custom-select-trigger');
  const triggerText = wrapper?.querySelector('.pn-cs-text');
  const dropdown = wrapper?.querySelector('.pn-custom-select-dropdown');
  const selectedOption = select.options[select.selectedIndex];

  if (triggerText) triggerText.textContent = selectedOption ? selectedOption.textContent : 'Selecione...';
  if (trigger) {
    trigger.disabled = select.disabled;
    trigger.setAttribute('aria-label', selectedOption ? selectedOption.textContent : 'Selecione');
    trigger.setAttribute('aria-expanded', 'false');
  }
  wrapper?.classList.toggle('disabled', Boolean(select.disabled));

  if (!dropdown) return;
  dropdown.innerHTML = '';
  let lastGroupLabel = '';
  Array.from(select.options).forEach((opt, idx) => {
    const groupLabel = opt.parentElement?.tagName === 'OPTGROUP'
      ? opt.parentElement.getAttribute('label') || ''
      : '';
    if (groupLabel && groupLabel !== lastGroupLabel) {
      const group = document.createElement('div');
      group.className = 'pn-cs-group';
      group.setAttribute('role', 'presentation');
      group.textContent = groupLabel;
      dropdown.appendChild(group);
      lastGroupLabel = groupLabel;
    }

    const item = document.createElement('div');
    item.className = `pn-cs-item${opt.disabled ? ' disabled' : ''}${idx === select.selectedIndex ? ' selected' : ''}${opt.dataset.recommended ? ' is-recommended' : ''}`;
    item.id = `${dropdown.id || select.id + '-listbox'}-option-${idx}`;
    item.setAttribute('role', 'option');
    item.setAttribute('aria-selected', String(idx === select.selectedIndex));
    item.setAttribute('aria-disabled', String(Boolean(opt.disabled)));
    item.textContent = opt.textContent;
    item.dataset.value = opt.value;
    item.dataset.optionIndex = String(idx);
    if (opt.dataset.reason) item.title = opt.dataset.reason;

    item.addEventListener('click', e => {
      e.stopPropagation();
      if (opt.disabled) return;
      select.selectedIndex = idx;
      syncCustomSelect(select);
      select.dispatchEvent(new Event('change', { bubbles: true }));
      select.dispatchEvent(new Event('input', { bubbles: true }));
      wrapper?.classList.remove('open');
    });

    dropdown.appendChild(item);
  });
}

function setMunicipioField(scope, municipio, options = {}) {
  const input = document.getElementById(`pn-sel-municipio-${scope}`);
  if (!input) return;
  const emitInput = options.emitInput !== false;
  const dispatchChange = options.dispatchChange === true;
  input.disabled = false;
  input.value = municipio || '';
  input.dataset.selected = municipio || '';
  input.placeholder = 'Digite ou selecione o municipio...';
  if (emitInput) input.dispatchEvent(new Event('input', { bubbles: true }));
  if (dispatchChange) input.dispatchEvent(new Event('change', { bubbles: true }));
}

function closeMunicipioTypeaheads() {
  getMunicipioTypeaheadConfigs().forEach(({ inputId, dropdownId }) => {
    const input = document.getElementById(inputId);
    const dropdown = document.getElementById(dropdownId);
    if (dropdown) {
      dropdown.style.display = 'none';
      dropdown.querySelectorAll('.pn-ta-item.active').forEach(item => item.classList.remove('active'));
      dropdown.querySelectorAll('[aria-selected="true"]').forEach(item => item.setAttribute('aria-selected', 'false'));
    }
    if (input) {
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
    }
  });
}

function getCurrentUfForScope(scope, municipio) {
  const recorte = STATE.recorte || getGlobalRecorte();
  if (scope !== 'mun' && recorte.complete) return recorte.uf;
  const value = document.getElementById(`pn-sel-uf-${scope}`)?.value;
  return value || inferUfForMunicipio(municipio);
}

function inferUfForMunicipio(municipio) {
  const needle = normalizeText(municipio || '');
  if (!needle || !STATE.catalogo?.municipios_por_uf) return '';
  for (const [uf, municipios] of Object.entries(STATE.catalogo.municipios_por_uf)) {
    if ((municipios || []).some(m => normalizeText(m) === needle)) return uf;
  }
  return '';
}

function startRenameSavedAnalysis(id) {
  STATE.editingAnalysisId = id;
  renderSavedAnalyses();
  window.setTimeout(() => {
    const escapedId = window.CSS?.escape ? CSS.escape(String(id)) : String(id).replace(/"/g, '\\"');
    const input = document.querySelector(`.pn-analysis-item[data-id="${escapedId}"] .pn-ai-rename-input`);
    if (input instanceof HTMLInputElement) {
      input.focus();
      input.select();
    }
  }, 0);
}

function saveRenameSavedAnalysis(id, item) {
  const analysis = getSavedAnalysis(id);
  const input = item?.querySelector('.pn-ai-rename-input');
  const title = input?.value.trim();
  if (!analysis || !title) {
    showToast('Informe um nome para a analise.', 'warning');
    return;
  }

  analysis.title = title;
  const now = new Date();
  analysis.updatedAt = now.toISOString();
  analysis.date = now.toLocaleString('pt-BR');
  STATE.editingAnalysisId = null;
  persistSavedAnalyses();
}

function duplicateSavedAnalysis(id) {
  const analysis = getSavedAnalysis(id);
  if (!analysis) return;

  const copy = JSON.parse(JSON.stringify(analysis));
  copy.id = createSavedAnalysisId();
  copy.title = buildSavedCopyLabel(getAnalysisDisplayLabel(analysis), STATE.analisesSalvas.map(getAnalysisDisplayLabel));
  copy.label = copy.title;
  copy.dedupeKey = `${analysis.dedupeKey || buildAnalysisDedupeKey(analysis.type, analysis)}|copy:${copy.id}`;
  const now = new Date();
  copy.createdAt = now.toISOString();
  copy.updatedAt = now.toISOString();
  copy.date = now.toLocaleString('pt-BR');
  copy.storageScope = 'local';
  copy.duplicatedFrom = analysis.id;
  STATE.analisesSalvas.unshift(copy);
  if (STATE.analisesSalvas.length > 50) STATE.analisesSalvas.pop();
  persistSavedAnalyses();
}

function exportSavedAnalysis(id) {
  const analysis = getSavedAnalysis(id);
  if (!analysis) return;

  const payload = {
    schema: 'gvi.painel.analysis.v1',
    exportedAt: new Date().toISOString(),
    analysis,
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `analise-${slugifySavedAnalysisFilename(getAnalysisDisplayLabel(analysis))}.json`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function deleteSavedAnalysis(id) {
  STATE.analisesSalvas = STATE.analisesSalvas.filter(a => String(a.id) !== String(id));
  if (String(STATE.editingAnalysisId) === String(id)) STATE.editingAnalysisId = null;
  persistSavedAnalyses();
}

function clearSavedAnalyses() {
  if (!STATE.analisesSalvas.length) {
    renderSavedAnalyses();
    return;
  }
  STATE.analisesSalvas = [];
  STATE.editingAnalysisId = null;
  persistSavedAnalyses();
  showToast('Histórico local limpo.', 'info');
}

// Global kept for older inline handlers cached by the browser.
window['deleteAnalysis'] = deleteSavedAnalysis;

// ==========================================
// UTILS
// ==========================================
function esc(str) {
  if (str == null) return '';
  const el = document.createElement('span');
  el.textContent = String(str);
  return el.innerHTML;
}

function escapeHTML(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatNum(n) {
  return (n || 0).toLocaleString('pt-BR');
}

function formatSignedNum(n) {
  const number = Number(n || 0);
  const sign = number > 0 ? '+' : '';
  return `${sign}${formatNum(number)}`;
}

function formatPercent(n) {
  return `${(Number(n) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

function formatPercentCompact(n) {
  return `${(Number(n) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 2 })}%`;
}

function normalizeText(str) {
  return String(str || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function titleCase(str) {
  return String(str || '').replace(/\w\S*/g, word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
}

function debounce(fn, ms) {
  let timer;
  return function(...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), ms);
  };
}

// ==========================================
// CUSTOM SELECTS SYSTEM (GVI-Select)
// ==========================================
function initCustomSelects() {
  const selects = document.querySelectorAll('select.pn-select');
  
  selects.forEach((select, selectIndex) => {
    // Evita duplicar a inicialização no mesmo elemento
    if (select.dataset.customInitialized) return;
    select.dataset.customInitialized = 'true';
    select.setAttribute('aria-hidden', 'true');
    select.tabIndex = -1;
    
    // 1. Criar estrutura do custom select
    const container = document.createElement('div');
    container.className = 'pn-custom-select-container';
    if (select.disabled) container.classList.add('disabled');
    
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'pn-custom-select-trigger';
    trigger.setAttribute('role', 'combobox');
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    
    const triggerText = document.createElement('span');
    triggerText.className = 'pn-cs-text';
    
    const triggerArrow = document.createElement('span');
    triggerArrow.className = 'pn-cs-arrow';
    triggerArrow.textContent = '▼';
    
    trigger.appendChild(triggerText);
    trigger.appendChild(triggerArrow);
    
    const dropdown = document.createElement('div');
    dropdown.className = 'pn-custom-select-dropdown';
    const selectBaseId = select.id || `pn-custom-select-${selectIndex}`;
    const listboxId = `${selectBaseId}-listbox`;
    dropdown.id = listboxId;
    dropdown.setAttribute('role', 'listbox');
    trigger.setAttribute('aria-controls', listboxId);
    
    // Inserir container antes do select original e mover o select para dentro dele
    select.parentNode.insertBefore(container, select);
    container.appendChild(select);
    container.appendChild(trigger);
    container.appendChild(dropdown);

    let activeIndex = select.selectedIndex >= 0 ? select.selectedIndex : 0;
    let typeaheadBuffer = '';
    let typeaheadTimer;
    
    // Função para atualizar o valor exibido no trigger
    const updateTriggerText = () => {
      const selectedOption = select.options[select.selectedIndex];
      triggerText.textContent = selectedOption ? selectedOption.textContent : 'Selecione...';
      trigger.setAttribute('aria-label', triggerText.textContent || 'Selecione');
    };
    
    // Função para sincronizar a classe selecionada nos itens do dropdown
    const syncSelectedClass = () => {
      getDropdownItems().forEach(child => {
        const isSelected = Number(child.dataset.optionIndex) === select.selectedIndex;
        child.classList.toggle('selected', isSelected);
        child.setAttribute('aria-selected', String(isSelected));
      });
    };

    const getDropdownItems = () => Array.from(dropdown.querySelectorAll('.pn-cs-item'));

    const setActiveIndex = (idx, scrollIntoView = false) => {
      const options = getDropdownItems();
      if (options.length === 0 || select.options.length === 0) return;
      activeIndex = Math.max(0, Math.min(idx, select.options.length - 1));
      options.forEach(child => {
        child.classList.toggle('active', Number(child.dataset.optionIndex) === activeIndex);
      });
      const activeOption = options.find(child => Number(child.dataset.optionIndex) === activeIndex);
      if (activeOption) {
        trigger.setAttribute('aria-activedescendant', activeOption.id);
        if (scrollIntoView) activeOption.scrollIntoView({ block: 'nearest' });
      }
    };

    const dispatchSelectEvents = () => {
      select.dispatchEvent(new Event('change', { bubbles: true }));
      select.dispatchEvent(new Event('input', { bubbles: true }));
    };

    const selectOption = (idx) => {
      if (idx < 0 || idx >= select.options.length || select.options[idx].disabled) return;
      select.selectedIndex = idx;
      updateTriggerText();
      syncSelectedClass();
      setActiveIndex(idx, true);
      dispatchSelectEvents();
      closeDropdown();
    };
    
    // Função para renderizar as opções do dropdown
    const renderOptions = () => {
      dropdown.innerHTML = '';
      let lastGroupLabel = '';
      Array.from(select.options).forEach((opt, idx) => {
        const groupLabel = opt.parentElement?.tagName === 'OPTGROUP'
          ? opt.parentElement.getAttribute('label') || ''
          : '';
        if (groupLabel && groupLabel !== lastGroupLabel) {
          const group = document.createElement('div');
          group.className = 'pn-cs-group';
          group.setAttribute('role', 'presentation');
          group.textContent = groupLabel;
          dropdown.appendChild(group);
          lastGroupLabel = groupLabel;
        }

        const item = document.createElement('div');
        item.className = `pn-cs-item${opt.disabled ? ' disabled' : ''}${opt.dataset.recommended ? ' is-recommended' : ''}`;
        item.id = `${listboxId}-option-${idx}`;
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', String(idx === select.selectedIndex));
        item.setAttribute('aria-disabled', String(Boolean(opt.disabled)));
        if (idx === select.selectedIndex) item.classList.add('selected');
        item.textContent = opt.textContent;
        item.dataset.value = opt.value;
        item.dataset.optionIndex = String(idx);
        if (opt.dataset.reason) item.title = opt.dataset.reason;
        
        item.addEventListener('click', (e) => {
          e.stopPropagation();
          if (opt.disabled) return;
          selectOption(idx);
        });
        
        dropdown.appendChild(item);
      });
      setActiveIndex(select.selectedIndex >= 0 ? select.selectedIndex : 0);
    };
    
    const openDropdown = () => {
      if (select.disabled) return;
      // Fecha todos os outros dropdowns
      document.querySelectorAll('.pn-custom-select-container.open').forEach(c => {
        if (c !== container) {
          c.classList.remove('open');
          c.querySelector('.pn-custom-select-trigger')?.setAttribute('aria-expanded', 'false');
          const card = c.closest('.pn-card');
          if (card) card.classList.remove('focus-select');
        }
      });
      
      container.classList.add('open');
      trigger.setAttribute('aria-expanded', 'true');
      setActiveIndex(select.selectedIndex >= 0 ? select.selectedIndex : 0, true);
      const card = container.closest('.pn-card');
      if (card) card.classList.add('focus-select');
    };
    
    const closeDropdown = () => {
      container.classList.remove('open');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.removeAttribute('aria-activedescendant');
      const card = container.closest('.pn-card');
      if (card) card.classList.remove('focus-select');
    };

    const isOpen = () => container.classList.contains('open');

    const moveActive = (delta) => {
      if (!isOpen()) openDropdown();
      setActiveIndex(activeIndex + delta, true);
    };

    const searchByTyping = (key) => {
      clearTimeout(typeaheadTimer);
      typeaheadBuffer += key.toLocaleLowerCase('pt-BR');
      typeaheadTimer = setTimeout(() => { typeaheadBuffer = ''; }, 700);

      const normalizedBuffer = normalizeText(typeaheadBuffer);
      const options = Array.from(select.options);
      const matchIndex = options.findIndex(opt => normalizeText(opt.textContent).startsWith(normalizedBuffer))
        ?? -1;
      const fallbackIndex = matchIndex >= 0
        ? matchIndex
        : options.findIndex(opt => normalizeText(opt.textContent).includes(normalizedBuffer));
      if (fallbackIndex >= 0) {
        if (!isOpen()) openDropdown();
        setActiveIndex(fallbackIndex, true);
      }
    };
    
    // Toggle dropdown
    trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      if (select.disabled) return;
      if (container.classList.contains('open')) {
        closeDropdown();
      } else {
        openDropdown();
      }
    });

    trigger.addEventListener('keydown', (e) => {
      if (select.disabled) return;

      switch (e.key) {
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (isOpen()) {
            selectOption(activeIndex);
          } else {
            openDropdown();
          }
          break;
        case 'Escape':
          if (isOpen()) {
            e.preventDefault();
            closeDropdown();
          }
          break;
        case 'ArrowDown':
          e.preventDefault();
          moveActive(1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          moveActive(-1);
          break;
        case 'Home':
          e.preventDefault();
          if (!isOpen()) openDropdown();
          setActiveIndex(0, true);
          break;
        case 'End':
          e.preventDefault();
          if (!isOpen()) openDropdown();
          setActiveIndex(select.options.length - 1, true);
          break;
        default:
          if (e.key.length === 1 && !e.altKey && !e.ctrlKey && !e.metaKey) {
            searchByTyping(e.key);
          }
      }
    });
    
    // Fechar ao clicar fora
    document.addEventListener('click', () => {
      closeDropdown();
    });
    
    // Monitorar desativação/ativação do select nativo
    const checkDisabled = () => {
      if (select.disabled) {
        container.classList.add('disabled');
        trigger.disabled = true;
        trigger.setAttribute('aria-disabled', 'true');
      } else {
        container.classList.remove('disabled');
        trigger.disabled = false;
        trigger.setAttribute('aria-disabled', 'false');
      }
    };
    
    // Observer para capturar mudanças dinâmicas nas opções do select original
    const observer = new MutationObserver(() => {
      renderOptions();
      updateTriggerText();
      checkDisabled();
    });
    
    observer.observe(select, {
      childList: true,
      attributes: true,
      attributeFilter: ['disabled']
    });
    
    // Sobrescrever value/selectedIndex para sincronizar quando alterados programaticamente pelo JS
    const origValueDescriptor = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value');
    const origSelectedIndexDescriptor = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'selectedIndex');
    
    Object.defineProperty(select, 'value', {
      get() { return origValueDescriptor.get.call(this); },
      set(val) {
        origValueDescriptor.set.call(this, val);
        updateTriggerText();
        syncSelectedClass();
      },
      configurable: true
    });
    
    Object.defineProperty(select, 'selectedIndex', {
      get() { return origSelectedIndexDescriptor.get.call(this); },
      set(idx) {
        origSelectedIndexDescriptor.set.call(this, idx);
        updateTriggerText();
        syncSelectedClass();
      },
      configurable: true
    });
    
    // Sincroniza em alterações de eventos normais
    select.addEventListener('change', () => {
      updateTriggerText();
      syncSelectedClass();
    });
    
    // Inicialização do estado
    renderOptions();
    updateTriggerText();
    checkDisabled();
  });
}

// Observer global para detectar novos selects adicionados de forma dinâmica no DOM
const globalSelectObserver = new MutationObserver((mutations) => {
  let hasNewSelects = false;
  mutations.forEach(m => {
    m.addedNodes.forEach(node => {
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      if (node.classList?.contains('pn-select') || node.querySelector?.('.pn-select')) {
        hasNewSelects = true;
      }
    });
  });
  if (hasNewSelects) {
    initCustomSelects();
  }
});
globalSelectObserver.observe(document.body, { childList: true, subtree: true });
